import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
    CalendarClock, Plus, Trash2, Check, ChevronLeft, ChevronRight, Save,
    Lock, Search, ClipboardList, Download, Settings, ArrowLeft, Megaphone, RotateCcw,
    BarChart3, Sparkles, Loader2, MessageSquare, History, AlertTriangle, TrendingUp,
    TrendingDown, Minus, FileSpreadsheet, Hourglass, Flag
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Modal from './common/Modal';
import axiosInstance from '../utils/axiosConfig';
import { NumeroRodante } from './Proyectos/utils/motion';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Tope del comentario que acompaña al anuncio de inicio de operaciones (igual al backend)
const MAX_COMENTARIO_ANUNCIO = 600;

// De 8:00 a 23:55, cada 5 minutos (hay cierres encadenados a 3:05 y 3:10; finanzas cierra de noche)
const HORAS_OPTIONS = [];
for (let h = 8; h <= 23; h++) {
    for (let m = 0; m < 60; m += 5) {
        HORAS_OPTIONS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
}

const DEPARTAMENTOS = [
    { id: 'comercializacion', nombre: 'Comercializacion' },
    { id: 'distribucion', nombre: 'Distribucion y Facturacion' },
    { id: 'logistica', nombre: 'Logistica' },
    { id: 'costos', nombre: 'Costos y Presupuesto' },
    { id: 'cuentas_pagar', nombre: 'Cuentas por Pagar' },
    { id: 'credito_cobranza', nombre: 'Credito y Cobranza' },
    { id: 'tesoreria', nombre: 'Tesoreria' },
    { id: 'almacen', nombre: 'Almacen' },
    { id: 'comercio_ext', nombre: 'Comercio Exterior' },
    { id: 'compras', nombre: 'Compras' },
    { id: 'mercadeo', nombre: 'Mercadeo/Trade Marketing' },
    { id: 'its', nombre: 'TI' },
    { id: 'gestion_humana', nombre: 'Gestion Humana' },
    { id: 'impuestos', nombre: 'Impuestos' },
    { id: 'contabilidad', nombre: 'Contabilidad' },
];
// Planificación Financiera se muestra con el inicio de operaciones (sin su lista de precios no se arranca)
const DEPTO_INICIO = 'planificacion_financiera';

// Agrupa los renglones por fecha de cierre, en el orden en que vienen
const porDia = (items) => {
    const mapa = new Map();
    items.forEach(item => {
        const clave = item.fechaCierre || '';
        if (!mapa.has(clave)) mapa.set(clave, []);
        mapa.get(clave).push(item);
    });
    return [...mapa.entries()].map(([fecha, delDia]) => ({ fecha: fecha || null, items: delDia }));
};

// Botones del modulo: un solo juego para todas las vistas
const BTN = {
    primario: 'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-sura-blue text-white text-sm font-medium shadow-sm hover:bg-sura-blue-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue/40 focus-visible:ring-offset-2',
    secundario: 'inline-flex items-center justify-center gap-2 h-9 px-3.5 rounded-lg bg-white text-slate-700 text-sm font-medium ring-1 ring-inset ring-slate-200 shadow-sm hover:bg-slate-50 hover:text-slate-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
    fantasma: 'inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-slate-500 text-sm font-medium hover:bg-slate-100 hover:text-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
    exito: 'inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium shadow-sm hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2',
    icono: 'inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
};
const CAMPO_BASE = 'h-9 px-3 rounded-lg bg-white text-sm text-slate-800 ring-1 ring-inset ring-slate-200 outline-none focus:ring-2 focus:ring-sura-blue/30 transition-shadow';
const CAMPO = `${CAMPO_BASE} w-full`;
const TARJETA_SIN_BORDE = 'bg-white rounded-2xl ring-1 shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
const TARJETA = `${TARJETA_SIN_BORDE} ring-slate-200/80`;

const getInitials = (emp) => {
    const first = emp.nombre?.charAt(0) || '';
    const last = emp.apellido?.charAt(0) || emp.nombre?.split(' ')[1]?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
};

const getFullName = (emp) => {
    const nombre = (emp.nombre || '').split(' ')[0];
    const apellido = (emp.apellido || '').split(' ')[0];
    if (apellido) return `${nombre} ${apellido}`;
    return nombre || 'Sin nombre';
};

const parseUtcTimestamp = (value) => {
    if (!value) return null;
    const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/.test(value);
    const parsed = new Date(hasTimezone ? value : `${value}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatFechaCierre = (value) => {
    const fecha = parseUtcTimestamp(value);
    if (!fecha) return null;

    return {
        fecha: fecha.toLocaleDateString('es-VE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'America/Caracas',
        }),
        hora: hora12(fecha.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
            timeZone: 'America/Caracas',
        })),
    };
};

const formatDuracion = (milliseconds) => {
    const minutosTotales = Math.max(1, Math.round(Math.abs(milliseconds) / 60000));
    const dias = Math.floor(minutosTotales / 1440);
    const horas = Math.floor((minutosTotales % 1440) / 60);
    const minutos = minutosTotales % 60;
    const partes = [];

    if (dias > 0) partes.push(`${dias} d`);
    if (horas > 0) partes.push(`${horas} h`);
    if (minutos > 0 && partes.length < 2) partes.push(`${minutos} min`);

    return partes.slice(0, 2).join(' ');
};

// Fechas 'AAAA-MM-DD' del calendario, leidas como dia local
const fechaLocal = (iso) => new Date(`${iso}T12:00:00`);
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const diaLargo = (iso) => {
    const d = fechaLocal(iso);
    return `${d.toLocaleDateString('es-VE', { weekday: 'long' })} ${d.getDate()} de ${MESES[d.getMonth()].toLowerCase()}`;
};
const diaCorto = (iso) => {
    const d = fechaLocal(iso);
    const semana = d.toLocaleDateString('es-VE', { weekday: 'short' }).replace('.', '');
    return `${semana.charAt(0).toUpperCase()}${semana.slice(1)} ${d.getDate()}`;
};
const minutosDe = (hora) => {
    const [h, m] = (hora || '').split(':').map(Number);
    return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};
// Las horas se guardan 'HH:MM' (24 h) pero se muestran con am/pm: '14:05' -> { hm: '2:05', sufijo: 'pm' }
const partes12 = (hhmm) => {
    const min = minutosDe(hhmm);
    if (min === null) return null;
    const h = Math.floor(min / 60) % 24;
    return { hm: `${h % 12 === 0 ? 12 : h % 12}:${String(min % 60).padStart(2, '0')}`, sufijo: h < 12 ? 'am' : 'pm' };
};
const hora12 = (hhmm) => {
    const p = partes12(hhmm);
    return p ? `${p.hm} ${p.sufijo}` : '';
};

// ========== PRELACIÓN ==========
// Misma cuenta que calcular_prelacion en el backend (calendario_cierre_routes.py).
// Un renglón no se puede cerrar mientras espere a otro. Si el anterior cierra tarde,
// el plazo del que espera se corre: tiene su margen contado desde que el anterior
// cerró de verdad. Mismo día: la diferencia entre las dos horas; días distintos: 4 h.
// Ese corrimiento es retraso del anterior; lo que pase de ahí es retraso propio.
const MARGEN_OTRO_DIA_MS = 4 * 3600000;

const nombreRenglon = (item) =>
    item.nombre || DEPARTAMENTOS.find(d => d.id === item.departamentoId)?.nombre || item.departamentoId;

const planDe = (item) => {
    if (!item.fechaCierre) return null;
    const plan = new Date(`${item.fechaCierre}T${item.horaEntrega || '17:00'}:00-04:00`);
    return Number.isNaN(plan.getTime()) ? null : plan;
};

const cierreRealDe = (item) => {
    const log = item.checksLog || [];
    return item.completado && log.length > 0 ? parseUtcTimestamp(log[log.length - 1].fecha) : null;
};

// Map id -> { deps, esperando, vence, causa }. esperando: a quiénes todavía espera
// (no puede cerrar). vence: plazo efectivo (null si espera o no tiene fecha).
// causa: el renglón que le corrió el plazo.
const calcularPrelacion = (items) => {
    const porId = new Map(items.map(i => [i.id, i]));
    const resultado = new Map();
    items.forEach(item => {
        const deps = (item.dependeDe || [])
            .filter(id => id !== item.id && porId.has(id))
            .map(id => porId.get(id));
        const esperando = deps.filter(d => !d.completado);
        const plan = planDe(item);
        let vence = esperando.length > 0 ? null : plan;
        let causa = null;
        if (vence) {
            deps.forEach(d => {
                const planD = planDe(d);
                const cierreD = cierreRealDe(d);
                if (!planD || !cierreD) return;
                const margen = d.fechaCierre !== item.fechaCierre ? MARGEN_OTRO_DIA_MS : Math.max(0, plan - planD);
                const corrido = new Date(cierreD.getTime() + margen);
                if (corrido > vence) { vence = corrido; causa = d; }
            });
        }
        resultado.set(item.id, { deps, esperando, vence, causa });
    });
    return resultado;
};

// ¿`desde` espera (directa o indirectamente) a `hasta`? Para no ofrecer esperas
// que harían un círculo.
const esperaA = (desde, hasta, esperasDe, vistos = new Set()) => {
    if (desde === hasta) return true;
    if (vistos.has(desde)) return false;
    vistos.add(desde);
    return (esperasDe(desde) || []).some(d => esperaA(d, hasta, esperasDe, vistos));
};

// "28/10 17:00" si cae otro día que `respectoDe`, si no solo la hora
const cuandoCierra = (item, respectoDe) => {
    if (!item.fechaCierre) return hora12(item.horaEntrega);
    const [, mes, dia] = item.fechaCierre.split('-');
    const fecha = item.fechaCierre !== respectoDe ? `${dia}/${mes}` : '';
    return [fecha, hora12(item.horaEntrega)].filter(Boolean).join(' ');
};

// tono: ok | tarde | aviso | neutro (ver CHIP)
const getDiferenciaCierre = (item, quienCerro, ahora, prel) => {
    if (!item.fechaCierre || !item.horaEntrega) {
        return { texto: 'Sin plazo', detalle: 'Falta fecha u hora planificada', tono: 'neutro' };
    }

    const planificado = new Date(`${item.fechaCierre}T${item.horaEntrega}:00-04:00`);
    if (Number.isNaN(planificado.getTime())) {
        return { texto: 'Sin plazo', detalle: 'La fecha planificada no es válida', tono: 'neutro' };
    }

    if (!item.completado && prel?.esperando.length > 0) {
        const nombres = prel.esperando.map(nombreRenglon).join(', ');
        return { texto: 'En espera', detalle: `Se habilita cuando cierre ${nombres}`, tono: 'neutro' };
    }

    const cierreReal = parseUtcTimestamp(quienCerro?.fecha);
    if (item.completado && !cierreReal) {
        return { texto: 'Sin registro', detalle: 'Cierre forzado sin hora registrada', tono: 'neutro' };
    }

    // Plazo efectivo: el planificado, o el corrido si el anterior cerró tarde.
    // La diferencia se mide contra ese plazo: lo corrido es retraso del anterior.
    const plazo = prel?.vence || planificado;
    const corridoMs = plazo.getTime() - planificado.getTime();
    const corrido = prel?.causa && corridoMs >= 60000 ? {
        texto: `+${formatDuracion(corridoMs)} por ${nombreRenglon(prel.causa)}`,
        detalle: `El plazo se corrió ${formatDuracion(corridoMs)} porque ${nombreRenglon(prel.causa)} cerró tarde. Ese tiempo se le cuenta a ${nombreRenglon(prel.causa)}.`,
    } : null;
    const delPlazo = corrido ? 'del plazo corrido' : 'del plazo';

    const referencia = cierreReal || new Date(ahora);
    const diferencia = referencia.getTime() - plazo.getTime();

    if (cierreReal) {
        if (Math.abs(diferencia) < 60000) {
            return { texto: 'A tiempo', detalle: `Cerrado dentro del minuto ${delPlazo}`, tono: 'ok', corrido };
        }
        if (diferencia < 0) {
            const duracion = formatDuracion(diferencia);
            return { texto: `${duracion} antes`, detalle: `Cerrado ${duracion} antes ${delPlazo}`, tono: 'ok', corrido };
        }
        const duracion = formatDuracion(diferencia);
        return { texto: `+${duracion} tarde`, detalle: `Cerrado ${duracion} después ${delPlazo}`, tono: 'tarde', corrido };
    }

    if (diferencia > 0) {
        const duracion = formatDuracion(diferencia);
        return { texto: `+${duracion} vencido`, detalle: `Pendiente desde hace ${duracion}`, tono: 'tarde', corrido };
    }

    const duracion = formatDuracion(diferencia);
    return { texto: `Vence en ${duracion}`, detalle: `Tiempo restante para cerrar`, tono: 'aviso', corrido };
};

const CHIP = {
    ok: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15',
    tarde: 'bg-rose-50 text-rose-700 ring-rose-600/15',
    aviso: 'bg-amber-50 text-amber-800 ring-amber-600/20',
    neutro: 'bg-slate-100 text-slate-600 ring-slate-500/10',
};

// Estado de un renglón para la línea de tiempo: el color del nodo y del texto
const tonoDe = (item, prel, ahora) => {
    if (item.completado) return 'cerrado';
    if (prel?.esperando.length > 0) return 'espera';
    const plazo = prel?.vence || planDe(item);
    if (!plazo || !item.horaEntrega) return 'sinplazo';
    const falta = plazo.getTime() - ahora;
    if (falta < 0) return 'vencido';
    if (falta < 24 * 3600000) return 'pronto';
    return 'pendiente';
};

const TONO = {
    cerrado: { nodo: 'bg-emerald-500 border-emerald-500 text-white', texto: 'text-emerald-700', punto: 'bg-emerald-500 ring-white', nombre: 'Cerrado' },
    vencido: { nodo: 'bg-white border-rose-500', texto: 'text-rose-600', punto: 'bg-rose-500 ring-white', nombre: 'Vencido' },
    pronto: { nodo: 'bg-white border-amber-400', texto: 'text-amber-700', punto: 'bg-amber-400 ring-white', nombre: 'Vence pronto' },
    pendiente: { nodo: 'bg-white border-slate-300', texto: 'text-slate-600', punto: 'bg-slate-400 ring-white', nombre: 'Pendiente' },
    espera: { nodo: 'bg-slate-50 border-slate-300 border-dashed', texto: 'text-slate-500', punto: 'bg-white ring-slate-300', nombre: 'En espera' },
    sinplazo: { nodo: 'bg-white border-slate-200', texto: 'text-slate-400', punto: 'bg-slate-200 ring-white', nombre: 'Sin plazo' },
};

// ========== PIEZAS ==========
const Avatar = ({ emp, size = 28 }) => {
    const nombre = getFullName(emp || {});
    const estilo = { width: size, height: size };
    return emp?.foto ? (
        <img src={emp.foto} alt={nombre} title={nombre} style={estilo}
            className="rounded-full object-cover shrink-0 bg-slate-100 ring-1 ring-slate-200/70" />
    ) : (
        <span title={nombre} style={estilo}
            className="rounded-full bg-slate-200 text-slate-600 text-[9px] font-semibold tracking-tight flex items-center justify-center shrink-0">
            {getInitials(emp || {})}
        </span>
    );
};

// Todos los responsables, uno por línea con su foto y su nombre
const ListaResponsables = ({ ids, empleados, vacio = 'Sin responsable' }) => {
    if (!ids || ids.length === 0) return <span className="text-sm text-slate-300">{vacio}</span>;
    return (
        <ul className="space-y-1.5 min-w-0">
            {ids.map(id => {
                const emp = empleados.find(e => e.id === id);
                return (
                    <li key={id} className="flex items-center gap-2 min-w-0">
                        <Avatar emp={emp} size={24} />
                        <span className="text-sm font-medium text-slate-700 truncate" title={getFullName(emp || {})}>{getFullName(emp || {})}</span>
                    </li>
                );
            })}
        </ul>
    );
};

const EstadoPill = ({ estado }) => {
    const estilos = {
        borrador: { clase: 'bg-slate-100 text-slate-600', punto: 'bg-slate-400', texto: 'Borrador' },
        anunciado: { clase: 'bg-sura-blue/[0.07] text-sura-blue', punto: 'bg-sura-blue', texto: 'En curso' },
        completado: { clase: 'bg-emerald-50 text-emerald-700', punto: 'bg-emerald-500', texto: 'Completado' },
    };
    const e = estilos[estado] || estilos.borrador;
    return (
        <span className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-xs font-medium ${e.clase}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${e.punto} ${estado === 'anunciado' ? 'animate-pulse' : ''}`} />
            {e.texto}
        </span>
    );
};

// ========== HORA SELECT (8:00 am a 11:55 pm) ==========
const HoraSelect = ({ value, onChange, className = '' }) => (
    <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`${CAMPO} tabular-nums ${className}`}
    >
        <option value="">Sin hora</option>
        {HORAS_OPTIONS.map(h => <option key={h} value={h}>{hora12(h)}</option>)}
    </select>
);

// Cerrar un popover al hacer clic fuera
const useClicFuera = (ref, abierto, cerrar) => {
    useEffect(() => {
        if (!abierto) return undefined;
        const alClic = (event) => {
            if (ref.current && !ref.current.contains(event.target)) cerrar();
        };
        const alTecla = (event) => { if (event.key === 'Escape') cerrar(); };
        document.addEventListener('mousedown', alClic);
        document.addEventListener('keydown', alTecla);
        return () => {
            document.removeEventListener('mousedown', alClic);
            document.removeEventListener('keydown', alTecla);
        };
    }, [ref, abierto, cerrar]);
};

const PANEL_POPOVER = 'absolute z-40 mt-2 rounded-xl bg-white shadow-xl shadow-slate-900/10 ring-1 ring-slate-200';

// Fecha y hora de un renglón: la hora se ve grande en la agenda y al tocarla se cambian las dos
const ProgramarPopover = ({ fecha, hora, onCambiar, children }) => {
    const [abierto, setAbierto] = useState(false);
    const ref = useRef(null);
    const cerrar = useCallback(() => setAbierto(false), []);
    useClicFuera(ref, abierto, cerrar);
    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setAbierto(a => !a)} title="Cambiar fecha y hora"
                className="-mx-1.5 px-1.5 rounded-md hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue/30">
                {children}
            </button>
            {abierto && (
                <div className={`${PANEL_POPOVER} left-0 w-60 p-3 space-y-3`}>
                    <label className="block">
                        <span className="text-xs font-medium text-slate-500">Fecha</span>
                        <input type="date" value={fecha || ''}
                            onChange={(e) => onCambiar({ fechaCierre: e.target.value || null })}
                            className={`${CAMPO} mt-1`} />
                    </label>
                    <label className="block">
                        <span className="text-xs font-medium text-slate-500">Hora</span>
                        <HoraSelect value={hora} onChange={(v) => onCambiar({ horaEntrega: v })} className="mt-1" />
                    </label>
                </div>
            )}
        </div>
    );
};

// ========== RESPONSABLE POPOVER ==========
const ResponsablePopover = ({ empleados, value, onChange, loading }) => {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const cerrar = useCallback(() => { setIsOpen(false); setSearch(''); }, []);
    useClicFuera(containerRef, isOpen, cerrar);

    const filteredEmpleados = useMemo(() => {
        if (!search) return empleados.slice(0, 30);
        const s = search.toLowerCase();
        return empleados.filter(emp =>
            getFullName(emp).toLowerCase().includes(s) ||
            emp.departamento?.toLowerCase().includes(s) ||
            emp.cargo?.toLowerCase().includes(s)
        ).slice(0, 15);
    }, [empleados, search]);

    const toggleEmpleado = (emp) => {
        const newValue = value.includes(emp.id)
            ? value.filter(id => id !== emp.id)
            : [...value, emp.id];
        onChange(newValue);
    };

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center text-left rounded-lg -mx-1.5 px-1.5 py-1 hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue/30"
                title="Cambiar responsables"
            >
                {value.length === 0 ? (
                    <span className="inline-flex items-center gap-2 text-sm text-slate-400">
                        <span className="w-7 h-7 rounded-full border border-dashed border-slate-300 flex items-center justify-center shrink-0">
                            <Plus size={13} />
                        </span>
                        Asignar
                    </span>
                ) : (
                    <ListaResponsables ids={value} empleados={empleados} />
                )}
            </button>

            {isOpen && (
                <div className={`${PANEL_POPOVER} left-0 w-72`}>
                    <div className="p-2 border-b border-slate-100">
                        <div className="relative">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text" value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                autoFocus
                                className={`${CAMPO} pl-8 bg-slate-50`}
                                placeholder="Buscar persona"
                            />
                        </div>
                    </div>
                    <div className="max-h-64 overflow-auto p-1">
                        {loading ? (
                            <div className="p-3 text-center text-slate-500 text-xs">Cargando...</div>
                        ) : filteredEmpleados.length === 0 ? (
                            <div className="p-3 text-center text-slate-500 text-xs">Sin resultados</div>
                        ) : (
                            filteredEmpleados.map(emp => {
                                const elegido = value.includes(emp.id);
                                return (
                                    <button key={emp.id} type="button"
                                        onClick={() => toggleEmpleado(emp)}
                                        className={`w-full px-2 py-1.5 rounded-lg text-left flex items-center gap-2.5 transition-colors ${elegido ? 'bg-sura-blue/[0.06]' : 'hover:bg-slate-50'}`}
                                    >
                                        <Avatar emp={emp} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-900 truncate">{getFullName(emp)}</p>
                                            <p className="text-xs text-slate-500 truncate">{emp.cargo || 'Sin cargo'}</p>
                                        </div>
                                        {elegido && <Check size={15} className="text-sura-blue shrink-0" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ========== PRELACIÓN: A QUIÉN ESPERA ==========
// esperas: [{ clave, nombre, cuando, estado: 'cerrado' | 'pendiente' | null, detalle }]
const EsperaChips = ({ esperas, puedeEditar, discreto = false, onEditar, className = '' }) => {
    if (esperas.length === 0 && !puedeEditar) return null;
    return (
        <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
            {esperas.length > 0 && <span className="text-xs text-slate-400">Espera a</span>}
            {esperas.map(e => (
                <span key={e.clave} title={e.detalle}
                    className={`inline-flex items-center gap-1 max-w-full rounded-md px-1.5 py-0.5 text-xs leading-tight ring-1 ring-inset ${
                        e.estado === 'cerrado' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/15' : 'bg-white text-slate-600 ring-slate-200'
                    }`}>
                    {e.color && <PuntoCarril color={e.color} />}
                    {e.estado === 'cerrado' && <Check size={11} strokeWidth={3} className="shrink-0" />}
                    {!e.color && e.estado === 'pendiente' && <Hourglass size={11} className="shrink-0 text-slate-400" />}
                    <span className="truncate">{e.nombre}</span>
                    {e.cuando && <span className="shrink-0 tabular-nums text-slate-400">{e.cuando}</span>}
                </span>
            ))}
            {puedeEditar && (
                <button type="button" onClick={onEditar}
                    className={`text-xs text-slate-400 hover:text-sura-blue rounded px-1 py-0.5 transition-opacity focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue/30 ${
                        discreto && esperas.length === 0 ? 'opacity-0 group-hover:opacity-100' : ''
                    }`}>
                    {esperas.length > 0 ? 'Cambiar' : '+ A quién espera'}
                </button>
            )}
        </div>
    );
};

// opciones: [{ clave, nombre, cuando, indicaciones, bloqueada, despues }]
// bloqueada: ya espera (directa o indirectamente) a este renglón; marcarla haría un círculo.
// despues: cierra más tarde que este renglón, así que su hora no cuadra.
const EsperaModal = ({ nombre, cuando, opciones, seleccion, guardando, onCerrar, onGuardar }) => {
    const [marcadas, setMarcadas] = useState(() => seleccion || []);
    const alternar = (clave) => setMarcadas(prev =>
        prev.includes(clave) ? prev.filter(c => c !== clave) : [...prev, clave]
    );

    return (
        <Modal isOpen onClose={() => !guardando && onCerrar()} size="lg">
            <Modal.Header onClose={() => !guardando && onCerrar()}>
                <div className="flex items-center gap-3">
                    <Hourglass size={18} className="text-slate-400" />
                    <span>A quién espera {nombre}{cuando ? ` (${cuando})` : ''}</span>
                </div>
            </Modal.Header>
            <Modal.Body className="p-6">
                <p className="text-sm text-slate-600 mb-4">
                    No se podrá cerrar hasta que cierren los que marques. Si alguno cierra tarde, el plazo se corre y ese retraso se le cuenta al que se atrasó.
                </p>
                {opciones.length === 0 ? (
                    <p className="text-sm text-slate-500">No hay otros renglones.</p>
                ) : (
                    <div className="max-h-[50vh] overflow-y-auto -mx-2 space-y-0.5">
                        {opciones.map(op => {
                            const marcada = marcadas.includes(op.clave);
                            return (
                                <label key={op.clave}
                                    className={`flex items-start gap-3 px-2 py-2.5 rounded-lg transition-colors ${op.bloqueada ? 'opacity-50 cursor-not-allowed' : marcada ? 'bg-sura-blue/[0.05] cursor-pointer' : 'cursor-pointer hover:bg-slate-50'}`}>
                                    <input type="checkbox" checked={marcada} disabled={op.bloqueada || guardando}
                                        onChange={() => alternar(op.clave)}
                                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-sura-blue focus:ring-sura-blue/30" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-baseline gap-2 min-w-0">
                                            <span className="text-sm font-medium text-slate-900 truncate">{op.nombre}</span>
                                            {op.cuando && <span className="text-xs text-slate-500 tabular-nums shrink-0">{op.cuando}</span>}
                                        </div>
                                        {op.indicaciones && <p className="text-xs text-slate-500 truncate" title={op.indicaciones}>{op.indicaciones}</p>}
                                        {op.bloqueada && <p className="text-xs text-slate-500 mt-0.5">Ya espera a este renglón</p>}
                                        {marcada && op.despues && (
                                            <p className="text-xs text-amber-700 flex items-center gap-1 mt-0.5">
                                                <AlertTriangle size={12} className="shrink-0" />
                                                Cierra después que este renglón; revisa las horas
                                            </p>
                                        )}
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex gap-2 justify-end">
                    <button onClick={onCerrar} disabled={guardando} className={BTN.fantasma}>Cancelar</button>
                    <button onClick={() => onGuardar(marcadas)} disabled={guardando} className={BTN.primario}>
                        {guardando ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        Guardar
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

// ========== LA CUENTA ==========
// Cuenta regresiva al próximo plazo pendiente (antes del cierre, al primero), con el
// nombre de quién cierra. Si lo único pendiente está vencido, cuenta hacia arriba
// desde el plazo más viejo. Los vencidos salen debajo con su retraso.
// Tiene su propio reloj de un segundo para no redibujar toda la página.
const relojDe = (ms) => {
    const total = Math.max(0, Math.floor(Math.abs(ms) / 1000));
    const d = Math.floor(total / 86400);
    const hh = String(Math.floor((total % 86400) / 3600)).padStart(2, '0');
    const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    return { d, hms: `${hh}:${mm}:${ss}` };
};

// "Costos", "Costos y Contabilidad", "Costos, Contabilidad, Impuestos y 2 más"
const nombresJuntos = (items, max = 3) => {
    const nombres = items.map(nombreRenglon);
    if (nombres.length > max) return `${nombres.slice(0, max).join(', ')} y ${nombres.length - max} más`;
    return nombres.length > 1 ? `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}` : nombres[0];
};

// "hoy a las 12:30 pm", "mañana a las 9:00 am", "mié 28 a las 2:00 pm"
const cuandoEs = (fecha, ahora) => {
    const dia = isoLocal(fecha);
    const cuando = dia === isoLocal(new Date(ahora)) ? 'hoy'
        : dia === isoLocal(new Date(ahora + 86400000)) ? 'mañana'
            : diaCorto(dia).toLowerCase();
    const hora = hora12(`${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}`);
    return `${cuando} a las ${hora}`;
};

const CuentaCierre = ({ items, prelacion }) => {
    const [ahora, setAhora] = useState(() => Date.now());
    useEffect(() => {
        const t = window.setInterval(() => setAhora(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, []);

    // Los que se pueden cerrar ya (no esperan a nadie) y tienen plazo, del más viejo al más nuevo
    const porCerrar = items
        .filter(i => !i.completado && i.horaEntrega && !(prelacion.get(i.id)?.esperando.length > 0))
        .map(i => ({ item: i, plazo: prelacion.get(i.id)?.vence || planDe(i) }))
        .filter(x => x.plazo)
        .sort((a, b) => a.plazo - b.plazo);
    const vencidos = porCerrar.filter(x => x.plazo.getTime() < ahora);
    const proximos = porCerrar.filter(x => x.plazo.getTime() >= ahora);
    const pendientes = items.filter(i => !i.completado).length;

    if (pendientes === 0) {
        return (
            <div>
                <p className="text-sm text-slate-500">El cierre está listo</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Todos los departamentos cerraron</p>
            </div>
        );
    }

    // El número grande: el próximo plazo o, si no queda ninguno por delante, el vencido más viejo
    const foco = proximos[0] || vencidos[0];
    if (!foco) {
        return (
            <div>
                <p className="text-sm text-slate-500">Quedan por cerrar</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{pendientes} {pendientes === 1 ? 'departamento' : 'departamentos'}</p>
                <p className="mt-2 text-sm text-slate-500">Esperan a otro departamento o no tienen plazo.</p>
            </div>
        );
    }

    const atrasado = !proximos[0];
    const resta = foco.plazo.getTime() - ahora;
    const reloj = relojDe(resta);
    const juntos = porCerrar.filter(x => x.plazo.getTime() === foco.plazo.getTime());
    const primero = !atrasado && vencidos.length === 0 && items.every(i => !i.completado);
    const titulo = atrasado ? 'Se pasó del plazo hace' : primero ? 'El primer plazo vence en' : 'El próximo plazo vence en';
    const tono = atrasado ? 'text-rose-600' : resta < 3600000 ? 'text-amber-600' : 'text-slate-900';
    // Los vencidos que no están ya en la línea de arriba
    const otrosVencidos = vencidos.filter(x => !juntos.includes(x));

    return (
        <div className="min-w-0">
            <p className="text-sm text-slate-500">{titulo}</p>
            <p className={`mt-1 flex items-baseline tabular-nums ${tono}`} aria-live="off">
                {atrasado && <span className="text-3xl font-semibold mr-1">+</span>}
                {reloj.d > 0 && (
                    <>
                        <span className="text-[40px] leading-none font-semibold tracking-tight">{reloj.d}</span>
                        <span className="ml-1 mr-3 text-lg font-medium opacity-60">d</span>
                    </>
                )}
                <span className="text-[40px] leading-none font-semibold tracking-tight">{reloj.hms}</span>
            </p>
            <p className="mt-2 text-sm text-slate-500">
                <span className="font-medium text-slate-800">{nombresJuntos(juntos.map(x => x.item))}</span>
                {atrasado ? `, vencía ${cuandoEs(foco.plazo, ahora)}` : `, ${cuandoEs(foco.plazo, ahora)}`}
            </p>
            {otrosVencidos.length > 0 && (
                <p className="mt-1 text-sm text-rose-600">
                    {atrasado ? 'También vencidos: ' : 'Vencidos: '}
                    {otrosVencidos.slice(0, 3).map((x, i) => (
                        <span key={x.item.id}>
                            {i > 0 && ', '}
                            {nombreRenglon(x.item)} <span className="tabular-nums">+{formatDuracion(ahora - x.plazo.getTime())}</span>
                        </span>
                    ))}
                    {otrosVencidos.length > 3 && ` y ${otrosVencidos.length - 3} más`}
                </p>
            )}
        </div>
    );
};

// ========== RUTA DEL CIERRE ==========
// Los días de cierre como estaciones, de izquierda a derecha, hasta el inicio de
// operaciones. El tramo recorrido se pinta hasta hoy: en la estación del día, o a
// mitad de tramo si hoy no hay cierres (fin de semana).
const finDelDia = (iso) => new Date(`${iso}T00:00:00`).getTime() + 86400000;

const NodoRuta = ({ clase = '', halo, reduce, children }) => (
    <span className={`relative w-5 h-5 shrink-0 rounded-full flex items-center justify-center ${clase}`}>
        {halo && !reduce && (
            <motion.span aria-hidden="true" className="absolute inset-0 rounded-full bg-cyan-400"
                initial={{ scale: 1, opacity: 0.5 }}
                animate={{ scale: 2.2, opacity: 0 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }} />
        )}
        <span className="relative flex items-center justify-center">{children}</span>
    </span>
);

const RutaDias = ({ items, tonos, ahora, inicioOperaciones, inicioAnunciado }) => {
    const reduce = useReducedMotion();
    const hoy = isoLocal(new Date(ahora));
    const dias = [...new Set(items.filter(i => i.fechaCierre).map(i => i.fechaCierre))].sort();
    if (dias.length === 0) {
        return <p className="text-sm text-slate-400">Cuando los departamentos tengan fecha, aquí se ve la ruta del cierre.</p>;
    }

    const estaciones = dias.map(dia => {
        const delDia = items.filter(i => i.fechaCierre === dia)
            .sort((a, b) => (minutosDe(a.horaEntrega) ?? 0) - (minutosDe(b.horaEntrega) ?? 0));
        return {
            clave: dia,
            dia,
            total: delDia.length,
            cerrados: delDia.filter(i => i.completado).length,
            vencidos: delDia.filter(i => tonos.get(i.id) === 'vencido').length,
            detalle: delDia.map(i => `${hora12(i.horaEntrega) || 'Sin hora'}  ${nombreRenglon(i)}${i.completado ? ' (cerrado)' : ''}`).join('\n'),
        };
    });
    if (hoy < dias[0]) estaciones.unshift({ clave: 'hoy', dia: hoy, previa: true });
    estaciones.push({ clave: 'operaciones', dia: inicioOperaciones || null, operaciones: true });

    // Dónde está hoy: en una estación, o en el tramo que sale de la última que ya pasó
    const enEstacion = estaciones.findIndex(e => e.dia === hoy);
    let tramoHoy = -1;
    let fraccionHoy = 0;
    if (enEstacion < 0) {
        tramoHoy = estaciones.reduce((k, e, i) => (e.dia && e.dia < hoy ? i : k), -1);
        const siguiente = estaciones[tramoHoy + 1];
        if (siguiente?.dia) {
            const desde = finDelDia(estaciones[tramoHoy].dia);
            const hasta = new Date(`${siguiente.dia}T00:00:00`).getTime();
            fraccionHoy = hasta > desde ? Math.min(0.9, Math.max(0.1, (ahora - desde) / (hasta - desde))) : 0.5;
        } else if (siguiente) {
            fraccionHoy = 0.5;
        }
    }
    const avance = (i) => {
        if (enEstacion >= 0) return i < enEstacion ? 1 : 0;
        if (i < tramoHoy) return 1;
        return i === tramoHoy ? fraccionHoy : 0;
    };

    const nodo = (e, esHoy) => {
        if (e.previa) {
            return <NodoRuta halo reduce={reduce}><span className="w-2.5 h-2.5 rounded-full bg-sura-blue" /></NodoRuta>;
        }
        if (e.operaciones) {
            return (
                <NodoRuta halo={esHoy} reduce={reduce} clase={inicioAnunciado ? 'bg-emerald-500 text-white' : 'bg-white ring-2 ring-inset ring-slate-300 text-slate-400'}>
                    <Flag size={10} strokeWidth={2.5} />
                </NodoRuta>
            );
        }
        if (e.total > 0 && e.cerrados === e.total) {
            return (
                <NodoRuta halo={esHoy} reduce={reduce} clase="bg-emerald-500 text-white">
                    <Check size={11} strokeWidth={3} />
                </NodoRuta>
            );
        }
        const anillo = e.vencidos > 0 ? 'ring-rose-500' : esHoy ? 'ring-sura-blue' : e.dia < hoy ? 'ring-slate-400' : 'ring-slate-300';
        const punto = e.vencidos > 0 ? 'bg-rose-500' : esHoy ? 'bg-sura-blue' : null;
        return (
            <NodoRuta halo={esHoy} reduce={reduce} clase={`bg-white ring-2 ring-inset ${anillo}`}>
                {punto && <span className={`w-2 h-2 rounded-full ${punto}`} />}
            </NodoRuta>
        );
    };

    const etiqueta = (e, esHoy) => {
        if (e.previa) {
            return <p className="text-xs font-medium text-sura-blue">Hoy</p>;
        }
        if (e.operaciones) {
            return (
                <>
                    <p className="text-xs font-medium text-slate-700">Operaciones</p>
                    <p className={`mt-0.5 text-[11px] capitalize tabular-nums ${e.dia ? 'text-slate-500' : 'text-slate-400'}`}>
                        {e.dia ? diaCorto(e.dia) : 'Sin fecha'}
                        {e.dia && <span className="normal-case"> {MESES[fechaLocal(e.dia).getMonth()].slice(0, 3).toLowerCase()}</span>}
                    </p>
                    <p className={`text-[11px] ${inicioAnunciado ? 'text-emerald-600' : 'text-slate-400'}`}>{inicioAnunciado ? 'Anunciado' : 'Por anunciar'}</p>
                </>
            );
        }
        const listo = e.total > 0 && e.cerrados === e.total;
        return (
            <>
                <p className={`text-xs font-medium capitalize ${esHoy ? 'text-sura-blue' : 'text-slate-700'}`}>
                    {diaCorto(e.dia)}{esHoy && <span className="normal-case font-normal"> (hoy)</span>}
                </p>
                <p className={`mt-0.5 text-[11px] tabular-nums whitespace-nowrap ${listo ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {e.cerrados} de {e.total} cerrados
                </p>
                {e.vencidos > 0 && (
                    <p className="text-[11px] text-rose-600 tabular-nums">{e.vencidos} {e.vencidos === 1 ? 'vencido' : 'vencidos'}</p>
                )}
            </>
        );
    };

    return (
        <ol className="flex min-w-[520px] pt-4">
            {estaciones.map((e, i) => {
                const ultima = i === estaciones.length - 1;
                const esHoy = !e.previa && i === enEstacion;
                const recorrido = avance(i);
                return (
                    <li key={e.clave} className={ultima ? 'shrink-0' : 'flex-1 min-w-0'} title={e.detalle}>
                        <div className="flex items-center">
                            {nodo(e, esHoy)}
                            {!ultima && (
                                <div className="relative flex-1 h-0.5 mx-1.5 rounded-full bg-slate-200">
                                    {recorrido > 0 && (
                                        <div className="absolute inset-y-0 left-0 rounded-full bg-sura-blue" style={{ width: `${recorrido * 100}%` }} />
                                    )}
                                    {i === tramoHoy && (
                                        <span className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${fraccionHoy * 100}%` }}>
                                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 text-[11px] font-medium text-sura-blue">Hoy</span>
                                            <NodoRuta halo reduce={reduce}><span className="w-2.5 h-2.5 rounded-full bg-sura-blue ring-2 ring-white" /></NodoRuta>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="mt-2 pr-3">{etiqueta(e, esHoy)}</div>
                    </li>
                );
            })}
        </ol>
    );
};

// Cabecera del mes: lo que viene (o el estado del borrador), cuánto va y la ruta de días
const RutaCierre = ({ items, prelacion, ahora, inicioOperaciones, inicioAnunciado, estado }) => {
    const reduce = useReducedMotion();
    const total = items.length;
    const cerrados = items.filter(i => i.completado).length;
    const tonos = new Map(items.map(i => [i.id, tonoDe(i, prelacion.get(i.id), ahora)]));
    const vencidos = items.filter(i => tonos.get(i.id) === 'vencido').length;

    let titular = null;
    if (estado === 'anunciado') {
        titular = <CuentaCierre items={items} prelacion={prelacion} />;
    } else if (estado === 'completado') {
        titular = (
            <>
                <p className="text-sm text-slate-500">El cierre terminó</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Todos los departamentos cerraron</p>
            </>
        );
    } else if (estado === 'borrador') {
        titular = (
            <>
                <p className="text-sm text-slate-500">Borrador</p>
                <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Así quedaría el cierre</p>
                <p className="mt-1 text-sm text-slate-500">Solo lo ven los gestores hasta que se anuncie.</p>
            </>
        );
    }

    return (
        <motion.section
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`${TARJETA} mb-8`}
        >
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 p-6">
                <div className="flex-1 min-w-0">{titular}</div>
                <dl className="flex items-start gap-8 shrink-0">
                    <div>
                        <dt className="text-xs text-slate-500">Cerrados</dt>
                        <dd className="mt-1 flex items-baseline gap-1 tabular-nums">
                            <NumeroRodante valor={cerrados} className="text-2xl font-semibold text-slate-900" />
                            <span className="text-sm text-slate-400">de {total}</span>
                        </dd>
                    </div>
                    {estado !== 'borrador' && (
                        <div>
                            <dt className="text-xs text-slate-500">Vencidos</dt>
                            <dd className={`mt-1 text-2xl font-semibold tabular-nums ${vencidos > 0 ? 'text-rose-600' : 'text-slate-300'}`}>{vencidos}</dd>
                        </div>
                    )}
                </dl>
            </div>

            <div className="border-t border-slate-100 px-6 pt-5 pb-6 overflow-x-auto">
                <RutaDias items={items} tonos={tonos} ahora={ahora}
                    inicioOperaciones={inicioOperaciones} inicioAnunciado={inicioAnunciado} />
            </div>
        </motion.section>
    );
};

// ========== UNA PARADA DE LA AGENDA (un departamento) ==========
// Columnas: hora | nodo | departamento | responsables | estado | acciones.
// La espina une los nodos del mismo día; se dibuja a la altura del nodo.
// En rem, no en px: la raíz es de 14.4px (index.css) y padding, gap y nodo escalan con ella
const ESPINA_X = 'calc(1.25rem + 72px + 1rem + 10px)'; // px-5 + hora + gap-x-4 + medio nodo
const NODO_Y = 'calc(1rem + 0.25rem + 0.5rem)'; // py-4 + mt-1 + medio nodo (h-4)
// Alineado con el contenido: px-5 + hora (72) + gap + nodo (20) + gap
const SANGRIA_PANEL = 'ml-[calc(3.25rem_+_92px)] mr-5';

// ========== GRAFO DE PRELACIÓN (estilo git) ==========
// Va en el margen gris, a la derecha de la tabla: un punto por renglón a la altura
// de su nodo y, por cada espera, una línea del color del renglón que hace esperar,
// que sale de su punto, baja por su carril y entra al punto del que espera. Cruza
// de un día a otro sin cortes. Solo se dibuja si el margen alcanza.
const GRAFO = { tronco: 8, primerCarril: 24, carril: 11, radio: 6, margen: 20 };
// Sin esmeralda, rosa ni ámbar: esos ya dicen cerrado, vencido y por vencer
const COLORES_CARRIL = ['#0ea5e9', '#f97316', '#8b5cf6', '#84cc16', '#ec4899', '#14b8a6', '#6366f1', '#d946ef', '#06b6d4', '#3b82f6', '#c084fc'];
const PUNTO_TONO = {
    cerrado: '#10b981', vencido: '#f43f5e', pronto: '#f59e0b', pendiente: '#94a3b8', espera: '#cbd5e1', sinplazo: '#e2e8f0',
};

// Todo lo que bloquea a `id` y todo lo que `id` bloquea, de punta a punta
const cadenaDe = (id, prelacion, dependientes) => {
    const cadena = new Set([id]);
    const subir = (x) => (prelacion.get(x)?.deps || []).forEach(d => {
        if (!cadena.has(d.id)) { cadena.add(d.id); subir(d.id); }
    });
    const bajar = (x) => (dependientes.get(x) || []).forEach(h => {
        if (!cadena.has(h)) { cadena.add(h); bajar(h); }
    });
    subir(id);
    bajar(id);
    return cadena;
};

// Aristas con su carril. Cada una toma el primer carril libre; una que termina en
// un renglón puede seguir con la que sale de ese mismo renglón.
const aristasDelGrafo = (filas, prelacion, colorDe) => {
    const indice = new Map(filas.map((it, i) => [it.id, i]));
    const aristas = [];
    filas.forEach((it, t) => (prelacion.get(it.id)?.deps || []).forEach(d => {
        const s = indice.get(d.id);
        // Una espera hacia un renglón que va más abajo está mal configurada: sin línea
        if (s !== undefined && s < t) aristas.push({ clave: `${d.id}-${it.id}`, s, t, desde: d.id, hasta: it.id, color: colorDe(d.id) });
    }));
    aristas.sort((a, b) => a.s - b.s || a.t - b.t);
    const finDeCarril = [];
    aristas.forEach(a => {
        let k = finDeCarril.findIndex(f => f <= a.s);
        if (k === -1) { k = finDeCarril.length; finDeCarril.push(a.t); }
        else finDeCarril[k] = a.t;
        a.carril = k;
    });
    return { aristas, carriles: finDeCarril.length };
};

const anchoGrafo = (carriles) => GRAFO.primerCarril + Math.max(carriles, 1) * GRAFO.carril;

const GrafoPrelacion = ({ filas, aristas, ancho, nodosRef, agendaRef, prelacion, cadena, ahora }) => {
    const [medidas, setMedidas] = useState({ ys: new Map(), alto: 0, cabe: false });

    const medir = useCallback(() => {
        const agenda = agendaRef.current;
        if (!agenda) return;
        const caja = agenda.getBoundingClientRect();
        // El margen que queda hasta el borde del área con scroll
        const contenedor = agenda.closest('main') || document.documentElement;
        const libre = contenedor.getBoundingClientRect().right - caja.right;
        const ys = new Map();
        filas.forEach(it => {
            const el = nodosRef.current.get(it.id);
            if (!el) return;
            const r = el.getBoundingClientRect();
            ys.set(it.id, r.top + r.height / 2 - caja.top);
        });
        setMedidas({ ys, alto: agenda.offsetHeight, cabe: libre >= ancho + GRAFO.margen * 2 });
    }, [filas, nodosRef, agendaRef, ancho]);

    useLayoutEffect(() => {
        medir();
        // Las filas entran con un pequeño desplazamiento animado: se vuelve a medir al terminar
        const t = setTimeout(medir, 450);
        const ro = new ResizeObserver(() => medir());
        if (agendaRef.current) ro.observe(agendaRef.current);
        window.addEventListener('resize', medir);
        return () => { clearTimeout(t); ro.disconnect(); window.removeEventListener('resize', medir); };
    }, [medir, agendaRef]);

    const { ys, alto, cabe } = medidas;
    if (!cabe || aristas.length === 0 || ys.size === 0) return null;

    const xCarril = (k) => GRAFO.primerCarril + k * GRAFO.carril;
    const r = GRAFO.radio;
    const conPunto = filas.filter(it => ys.has(it.id));
    const yMin = Math.min(...conPunto.map(it => ys.get(it.id)));
    const yMax = Math.max(...conPunto.map(it => ys.get(it.id)));
    const enCadena = (a) => !!cadena && cadena.has(a.desde) && cadena.has(a.hasta);

    return (
        <svg aria-hidden="true" width={ancho} height={alto}
            className="hidden lg:block absolute top-0 !mt-0 pointer-events-none overflow-visible"
            style={{ left: `calc(100% + ${GRAFO.margen}px)` }}>
            <line x1={GRAFO.tronco} x2={GRAFO.tronco} y1={yMin} y2={yMax} stroke="#e2e8f0" strokeWidth="1" />
            {aristas.map(a => {
                const y1 = ys.get(a.desde);
                const y2 = ys.get(a.hasta);
                if (y1 === undefined || y2 === undefined) return null;
                const x = xCarril(a.carril);
                const d = `M ${GRAFO.tronco} ${y1} H ${x - r} Q ${x} ${y1} ${x} ${y1 + r} V ${y2 - r} Q ${x} ${y2} ${x - r} ${y2} H ${GRAFO.tronco}`;
                const resaltada = enCadena(a);
                return (
                    <path key={a.clave} d={d} fill="none" stroke={a.color}
                        strokeWidth={resaltada ? 2 : 1.5} strokeLinecap="round" strokeLinejoin="round"
                        style={{ opacity: cadena ? (resaltada ? 1 : 0.12) : 0.7, transition: 'opacity 200ms' }} />
                );
            })}
            {conPunto.map(it => {
                const tono = tonoDe(it, prelacion.get(it.id), ahora);
                const atenuado = cadena && !cadena.has(it.id);
                return (
                    <circle key={it.id} cx={GRAFO.tronco} cy={ys.get(it.id)} r={3.5}
                        fill={tono === 'espera' ? '#ffffff' : PUNTO_TONO[tono]}
                        stroke={tono === 'espera' ? PUNTO_TONO.espera : '#ffffff'} strokeWidth="1.5"
                        style={{ opacity: atenuado ? 0.3 : 1, transition: 'opacity 200ms' }} />
                );
            })}
        </svg>
    );
};

// Punto de color del renglón al que se espera (empareja con su carril)
const PuntoCarril = ({ color }) => (
    <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
);

const ParadaRow = ({ item, index, primero, ultimo, empleados, loadingEmpleados, onUpdate, onDelete, onToggleResponsableCheck, getDepartamentoNombre, isSuperAdmin, puedeGestionar, currentEmpleadoId, calendarioEstado, ahora, prel, onEditarEspera, cadena = null, onFoco, colorDe = () => undefined, registrarNodo }) => {
    const reduce = useReducedMotion();
    const locked = calendarioEstado !== 'borrador' && !isSuperAdmin;
    // Los responsables se pueden editar también después de publicar, pero solo
    // los gestores (admin/contabilidad). El resto de campos siguen con `locked`.
    const responsablesLocked = calendarioEstado !== 'borrador' && !puedeGestionar;
    const [editandoIndicaciones, setEditandoIndicaciones] = useState(false);
    const [tempValue, setTempValue] = useState('');
    const [indicacionesAbiertas, setIndicacionesAbiertas] = useState(false);
    const [comentarioText, setComentarioText] = useState('');
    const [showComentarios, setShowComentarios] = useState(false);
    const [showConfirmCheck, setShowConfirmCheck] = useState(false);
    const debounceRef = useRef(null);

    const isResponsable = (item.responsableIds || []).includes(currentEmpleadoId);
    const displayNombre = item.nombre || getDepartamentoNombre(item.departamentoId);
    const comentarios = item.comentarios || [];
    const checksLog = item.checksLog || [];
    const quienCerro = checksLog.length > 0 ? checksLog[checksLog.length - 1] : null;
    const fechaCierreReal = formatFechaCierre(quienCerro?.fecha);
    const diferenciaCierre = getDiferenciaCierre(item, quienCerro, ahora, prel);
    const enEspera = !item.completado && (prel?.esperando.length || 0) > 0;
    const tono = tonoDe(item, prel, ahora);
    const esperas = (prel?.deps || []).map(d => ({
        clave: d.id,
        nombre: nombreRenglon(d),
        cuando: cuandoCierra(d, item.fechaCierre),
        estado: d.completado ? 'cerrado' : 'pendiente',
        color: colorDe(d.id),
        detalle: `${d.completado ? 'Ya cerró' : 'Pendiente'}${d.indicaciones ? `: ${d.indicaciones.trim()}` : ''}`,
    }));
    const indicacionesLargas = (item.indicaciones || '').length > 140;

    // El cierre ocurre a la vista (no venia cerrado al cargar): el nodo se llena
    // con un check y una onda, y aparecen la hora y quien cerro. Se calcula en el
    // render del cambio, que es cuando montan esas piezas.
    const estabaCerrado = useRef(item.completado);
    const cierreNuevo = item.completado && !estabaCerrado.current;
    const [mostrarOnda, setMostrarOnda] = useState(false);
    useEffect(() => {
        if (item.completado && !estabaCerrado.current) setMostrarOnda(true);
        estabaCerrado.current = item.completado;
    }, [item.completado]);
    useEffect(() => {
        if (!mostrarOnda) return undefined;
        const t = setTimeout(() => setMostrarOnda(false), 1200);
        return () => clearTimeout(t);
    }, [mostrarOnda]);
    const entraSiCierra = (desde) => (cierreNuevo && !reduce ? desde : false);

    const empezarIndicaciones = () => {
        if (locked) return;
        setTempValue(item.indicaciones || '');
        setEditandoIndicaciones(true);
    };

    const guardarIndicaciones = () => {
        setEditandoIndicaciones(false);
        if (locked) return;
        if ((tempValue || '') !== (item.indicaciones || '')) onUpdate(item.id, { indicaciones: tempValue || null });
    };

    const handleInlineResponsableChange = (ids) => {
        if (responsablesLocked) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onUpdate(item.id, { responsableIds: ids });
        }, 500);
    };

    const confirmarCierre = () => {
        onToggleResponsableCheck(item.id, comentarioText);
        setShowConfirmCheck(false);
        setComentarioText('');
    };

    const hora = (
        <span className={`block whitespace-nowrap text-[19px] leading-7 font-semibold tracking-tight tabular-nums ${item.horaEntrega ? (item.completado ? 'text-slate-400' : 'text-slate-900') : 'text-slate-300'}`}>
            {partes12(item.horaEntrega) ? (
                <>
                    {partes12(item.horaEntrega).hm}
                    <span className={`ml-0.5 text-xs font-medium tracking-normal ${item.completado ? 'text-slate-300' : 'text-slate-400'}`}>{partes12(item.horaEntrega).sufijo}</span>
                </>
            ) : '--:--'}
        </span>
    );

    return (
        <motion.div
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: Math.min(index, 12) * 0.025 }}
            className="group relative"
            onMouseEnter={() => onFoco?.(item.id)}
            onMouseLeave={() => onFoco?.(null)}
        >
            {/* Espina del día (en las filas siguientes sube 1px para tapar el divisor) */}
            {!(primero && ultimo) && (
                <span aria-hidden="true" className="absolute w-px -translate-x-1/2 bg-slate-200"
                    style={{ left: ESPINA_X, top: primero ? NODO_Y : '-1px', ...(ultimo ? { height: `calc(${NODO_Y} + 1px)` } : { bottom: 0 }) }} />
            )}

            <div className={`grid grid-cols-[72px_20px_minmax(0,1fr)] lg:grid-cols-[72px_20px_minmax(0,1fr)_220px_180px_auto] gap-x-4 gap-y-3 px-5 py-4 transition-opacity duration-200 ${cadena && !cadena.has(item.id) ? 'opacity-40' : ''}`}>
                {/* Hora */}
                <div>
                    {locked ? hora : (
                        <ProgramarPopover fecha={item.fechaCierre} hora={item.horaEntrega} onCambiar={(cambios) => onUpdate(item.id, cambios)}>
                            {hora}
                        </ProgramarPopover>
                    )}
                </div>

                {/* Nodo */}
                <div className="relative flex justify-center">
                    <span ref={(el) => registrarNodo?.(item.id, el)} className={`relative z-[1] mt-1 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-500 ${TONO[tono].nodo}`}
                        title={TONO[tono].nombre}>
                        {(cierreNuevo || mostrarOnda) && (
                            <motion.span aria-hidden="true"
                                initial={{ scale: 1, opacity: 0.6 }}
                                animate={{ scale: 2.6, opacity: 0 }}
                                transition={{ duration: 0.9, ease: 'easeOut', delay: 0.1 }}
                                className="absolute inset-0 rounded-full border-2 border-emerald-400" />
                        )}
                        {item.completado && (
                            <motion.span initial={entraSiCierra({ scale: 0, rotate: -30 })} animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: 'spring', stiffness: 520, damping: 16 }}>
                                <Check size={10} strokeWidth={3.5} />
                            </motion.span>
                        )}
                    </span>
                </div>

                {/* Departamento */}
                <div className="min-w-0">
                    <h4 className={`text-[15px] leading-7 font-semibold truncate ${item.completado ? 'text-slate-500' : 'text-slate-900'}`}>{displayNombre}</h4>
                    {editandoIndicaciones ? (
                        <textarea
                            autoFocus
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            onBlur={guardarIndicaciones}
                            onKeyDown={(e) => { if (e.key === 'Escape') setEditandoIndicaciones(false); }}
                            rows={3}
                            className="mt-1 w-full p-2.5 rounded-lg bg-white text-sm text-slate-700 ring-1 ring-inset ring-slate-200 outline-none focus:ring-2 focus:ring-sura-blue/30 resize-none"
                        />
                    ) : item.indicaciones ? (
                        <div className="mt-0.5">
                            <p onClick={locked ? undefined : empezarIndicaciones}
                                title={locked ? undefined : 'Clic para editar'}
                                className={`text-[13px] leading-relaxed text-slate-500 break-words whitespace-pre-line ${indicacionesLargas && !indicacionesAbiertas ? 'line-clamp-2' : ''} ${locked ? '' : 'cursor-text rounded-md -mx-1.5 px-1.5 hover:bg-slate-100 transition-colors'}`}>
                                {item.indicaciones}
                            </p>
                            {indicacionesLargas && (
                                <button type="button" onClick={() => setIndicacionesAbiertas(v => !v)}
                                    className="mt-0.5 text-xs text-slate-400 hover:text-sura-blue">
                                    {indicacionesAbiertas ? 'Ver menos' : 'Ver todo'}
                                </button>
                            )}
                        </div>
                    ) : !locked ? (
                        <button type="button" onClick={empezarIndicaciones}
                            className="mt-0.5 text-[13px] text-slate-300 hover:text-slate-500">
                            Agregar indicaciones
                        </button>
                    ) : null}
                    <EsperaChips esperas={esperas} puedeEditar={puedeGestionar} discreto
                        onEditar={() => onEditarEspera(item.id)} className={esperas.length > 0 || puedeGestionar ? 'mt-2' : ''} />
                </div>

                {/* Responsables */}
                <div className="col-start-3 lg:col-start-auto min-w-0 lg:pt-0.5">
                    {responsablesLocked ? (
                        <ListaResponsables ids={item.responsableIds || []} empleados={empleados} />
                    ) : (
                        <ResponsablePopover
                            empleados={empleados}
                            value={item.responsableIds || []}
                            onChange={handleInlineResponsableChange}
                            loading={loadingEmpleados}
                        />
                    )}
                </div>

                {/* Estado: lo que falta o cómo cerró */}
                <div className="col-start-3 lg:col-start-auto min-w-0 lg:pt-1">
                    {item.completado ? (
                        <>
                            <motion.span
                                initial={entraSiCierra({ opacity: 0, scale: 0.9 })}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ type: 'spring', stiffness: 360, damping: 24, delay: 0.25 }}
                                className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums ring-1 ring-inset ${CHIP[diferenciaCierre.tono]}`}
                                title={diferenciaCierre.detalle}
                            >
                                {diferenciaCierre.texto}
                            </motion.span>
                            {fechaCierreReal && (
                                <motion.p
                                    initial={entraSiCierra({ opacity: 0, y: 4 })}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.35 }}
                                    className="mt-1 text-xs text-slate-500 tabular-nums truncate"
                                    title={`Cerrado por ${quienCerro.nombre}`}
                                >
                                    {quienCerro.nombre?.split(' ')[0]} cerró el {fechaCierreReal.fecha.slice(0, 5)} a las {fechaCierreReal.hora}
                                </motion.p>
                            )}
                        </>
                    ) : (
                        <>
                            <p className={`text-sm font-medium tabular-nums ${TONO[tono].texto}`} title={diferenciaCierre.detalle}>
                                {diferenciaCierre.texto}
                            </p>
                            {enEspera && (
                                <div className="mt-1 text-xs text-slate-400">
                                    <p>Se habilita cuando cierre</p>
                                    <ul className="mt-0.5 space-y-0.5">
                                        {prel.esperando.map(d => (
                                            <li key={d.id} className="flex items-center gap-1.5 min-w-0" title={cuandoCierra(d, null)}>
                                                <PuntoCarril color={colorDe(d.id)} />
                                                <span className="truncate text-slate-500">{nombreRenglon(d)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                    {/* Lo que el anterior le corrió el plazo: no cuenta como retraso de este renglón */}
                    {diferenciaCierre.corrido && (
                        <p className="mt-1 text-[11px] leading-tight text-slate-400 tabular-nums" title={diferenciaCierre.corrido.detalle}>
                            Plazo {diferenciaCierre.corrido.texto}
                        </p>
                    )}
                </div>

                {/* Acciones: Cerrar primero, pegado a la columna de estado */}
                <div className="col-start-3 lg:col-start-auto flex items-center lg:items-start gap-1">
                    {!item.completado && (isResponsable || isSuperAdmin) && (
                        enEspera ? (
                            <span title={`Se habilita cuando cierre ${prel.esperando.map(nombreRenglon).join(', ')}`}>
                                <button type="button" disabled
                                    className="inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-lg bg-slate-100 text-slate-400 text-sm font-medium cursor-not-allowed">
                                    <Lock size={13} />Cerrar
                                </button>
                            </span>
                        ) : (
                            <button type="button" onClick={() => setShowConfirmCheck(v => !v)}
                                className={BTN.primario} title="Cerrar este departamento">
                                <Check size={15} strokeWidth={2.5} />Cerrar
                            </button>
                        )
                    )}
                    {comentarios.length > 0 && (
                        <button type="button" onClick={() => setShowComentarios(v => !v)}
                            className={`${BTN.icono} w-auto px-2 gap-1 text-xs tabular-nums ${showComentarios ? 'bg-slate-100 text-slate-700' : ''}`}
                            title={showComentarios ? 'Ocultar notas' : 'Ver notas'}>
                            <MessageSquare size={14} />{comentarios.length}
                        </button>
                    )}
                    {isSuperAdmin && !item.completado && (
                        <button type="button" onClick={() => onDelete(item.id)}
                            className={`${BTN.icono} hover:bg-rose-50 hover:text-rose-600 opacity-0 group-hover:opacity-100 focus:opacity-100`}
                            title="Eliminar">
                            <Trash2 size={14} />
                        </button>
                    )}
                </div>
            </div>

            {/* Confirmación de cierre (irreversible) */}
            <AnimatePresence initial={false}>
                {showConfirmCheck && (
                    <motion.div key="confirmar"
                        initial={reduce ? false : { height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden">
                        <div className={`${SANGRIA_PANEL} mb-4 rounded-xl bg-slate-50 ring-1 ring-inset ring-slate-200 p-4`}>
                            <div className="flex items-start gap-2.5">
                                <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold text-slate-900">Cerrar {displayNombre}</p>
                                    <p className="text-sm text-slate-500 mt-0.5">No se puede deshacer. Verifica que todos los procesos estén completos.</p>
                                </div>
                            </div>
                            <div className="mt-3 flex flex-col sm:flex-row gap-2">
                                <input
                                    type="text"
                                    value={comentarioText}
                                    onChange={(e) => setComentarioText(e.target.value)}
                                    placeholder="Nota opcional, por ejemplo: enviado por correo a contabilidad"
                                    className={`${CAMPO} flex-1`}
                                    autoFocus
                                    onKeyDown={(e) => { if (e.key === 'Enter') confirmarCierre(); }}
                                />
                                <div className="flex gap-2 justify-end">
                                    <button type="button" onClick={() => { setShowConfirmCheck(false); setComentarioText(''); }} className={BTN.fantasma}>
                                        Cancelar
                                    </button>
                                    <button type="button" onClick={confirmarCierre} className={BTN.primario}>
                                        Confirmar cierre
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Notas */}
            <AnimatePresence initial={false}>
                {showComentarios && comentarios.length > 0 && (
                    <motion.div key="notas"
                        initial={reduce ? false : { height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden">
                        <ul className={`${SANGRIA_PANEL} mb-4 rounded-xl bg-slate-50 ring-1 ring-inset ring-slate-200 divide-y divide-slate-200/70`}>
                            {comentarios.map((c, i) => (
                                <li key={i} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                                    <span className="font-medium text-slate-800 whitespace-nowrap">{c.nombre}</span>
                                    <span className="text-slate-600 flex-1 break-words">{c.comentario}</span>
                                    <span className="text-xs text-slate-400 whitespace-nowrap tabular-nums">{new Date(c.fecha).toLocaleDateString('es-VE')}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// La plantilla se guarda borrando y recreando, así que su prelación viaja como
// posiciones (`orden`). En pantalla cada renglón se identifica por su clave.
const claveDe = (item) => item.id ?? item._uid;
const conEsperas = (items) => {
    const porOrden = new Map(items.map(i => [i.orden, claveDe(i)]));
    return items.map(i => ({
        ...i,
        esperaKeys: (i.dependeDe || []).map(o => porOrden.get(o)).filter(k => k !== undefined),
    }));
};
// En la plantilla no hay horas: si dos renglones se llaman igual, se distinguen
// por el comienzo de sus indicaciones.
const etiquetaPlantilla = (item, items) => {
    const nombre = nombreRenglon(item);
    const repetido = items.some(o => o !== item && nombreRenglon(o) === nombre);
    const ind = (item.indicaciones || '').trim();
    if (!repetido || !ind) return nombre;
    return `${nombre}: ${ind.length > 28 ? `${ind.slice(0, 28).trim()}…` : ind}`;
};

// Solo estas personas arman el calendario; mientras esta en borrador el resto no lo ve
const GESTORES_CALENDARIO = ['mcolmenares', 'nrivero', 'mtravieso', 'mbordones'];

// Encabezado de las vistas secundarias (plantilla, historial)
const EncabezadoVista = ({ titulo, subtitulo, onVolver, children }) => (
    <header className="mb-8">
        <button onClick={onVolver} className={`${BTN.fantasma} -ml-3 mb-2`}>
            <ArrowLeft size={16} />Calendario
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">{titulo}</h1>
                {subtitulo && <p className="mt-1 text-sm text-slate-500 max-w-2xl">{subtitulo}</p>}
            </div>
            <div className="flex items-center gap-2">{children}</div>
        </div>
    </header>
);

const EstadoVacio = ({ icono: Icono, titulo, children }) => (
    <div className={`${TARJETA} px-6 py-16 text-center`}>
        <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4">
            <Icono size={22} />
        </div>
        <h3 className="text-base font-semibold text-slate-900">{titulo}</h3>
        <div className="mt-1 text-sm text-slate-500">{children}</div>
    </div>
);

// ========== COMPONENTE PRINCIPAL ==========
const CalendarioCierreView = () => {
    const { user } = useAuth();
    const reduce = useReducedMotion();
    const isSuperAdmin = user?.role === 'admin'; // Puede todo: editar, cerrar cualquiera, borrar
    const isGestor = user?.role === 'admin' || GESTORES_CALENDARIO.includes((user?.username || '').toLowerCase()); // Puede crear/anunciar/plantilla

    const [currentView, setCurrentView] = useState('calendario'); // 'calendario' | 'plantilla' | 'historial'
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [direccionMes, setDireccionMes] = useState(1);
    const [initialLoading, setInitialLoading] = useState(true);

    // Historial y análisis IA
    const [historial, setHistorial] = useState([]);
    const [historialLoading, setHistorialLoading] = useState(false);
    const [analisis, setAnalisis] = useState(null);
    const [analisisLoading, setAnalisisLoading] = useState(false);

    const departamentos = DEPARTAMENTOS;
    const [empleados, setEmpleados] = useState([]);
    const [loadingEmpleados, setLoadingEmpleados] = useState(true);

    const [calendarioId, setCalendarioId] = useState(null);
    const [calendarioEstado, setCalendarioEstado] = useState('borrador');
    const [enPreparacion, setEnPreparacion] = useState(false);
    const [calendarioCierre, setCalendarioCierre] = useState([]);
    const [inicioOperaciones, setInicioOperaciones] = useState('');
    const [inicioAnunciado, setInicioAnunciado] = useState(false);
    const [inicioComentario, setInicioComentario] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [exportandoExcel, setExportandoExcel] = useState(false);

    const [plantillaItems, setPlantillaItems] = useState([]);
    const [nuevoDeptNombre, setNuevoDeptNombre] = useState('');
    const [loadingPlantilla, setLoadingPlantilla] = useState(false);
    const [savingPlantilla, setSavingPlantilla] = useState(false);
    const [plantillaModificada, setPlantillaModificada] = useState(false);

    const [anuncioModal, setAnuncioModal] = useState(false);
    const [comentarioAnuncio, setComentarioAnuncio] = useState('');
    // Prelación: { tipo: 'calendario' | 'plantilla', clave } del renglón que se está editando
    const [esperaModal, setEsperaModal] = useState(null);
    const [guardandoEspera, setGuardandoEspera] = useState(false);
    const [ahora, setAhora] = useState(() => Date.now());

    useEffect(() => {
        const timer = window.setInterval(() => setAhora(Date.now()), 60000);
        return () => window.clearInterval(timer);
    }, []);

    // Determinar mes inicial: si hay calendario activo (no completado), mostrar ese
    useEffect(() => {
        const initMonth = async () => {
            try {
                const response = await axiosInstance.get('/api/calendario-cierre/activo');
                if (response.data?.mes && response.data?.anio) {
                    setSelectedMonth(response.data.mes - 1);
                    setSelectedYear(response.data.anio);
                } else {
                    setSelectedMonth(new Date().getMonth());
                    setSelectedYear(new Date().getFullYear());
                }
            } catch {
                setSelectedMonth(new Date().getMonth());
                setSelectedYear(new Date().getFullYear());
            } finally {
                setInitialLoading(false);
            }
        };
        initMonth();
    }, []);

    // Fetch empleados
    useEffect(() => {
        const fetchEmpleados = async () => {
            setLoadingEmpleados(true);
            try {
                const response = await axiosInstance.get('/api/empleados');
                setEmpleados((response.data || []).filter(e => e.activo !== false));
            } catch (error) { console.error('Error fetching empleados:', error); }
            finally { setLoadingEmpleados(false); }
        };
        fetchEmpleados();
    }, []);

    const aplicarCalendario = (cal) => {
        setCalendarioId(cal.id);
        setCalendarioEstado(cal.estado || 'borrador');
        setCalendarioCierre(cal.items || []);
        setInicioOperaciones(cal.inicioOperaciones || '');
        setInicioAnunciado(!!cal.inicioAnunciado);
        setInicioComentario(cal.inicioComentario || null);
    };

    // Fetch calendario
    const fetchCalendario = useCallback(async () => {
        if (selectedMonth === null || selectedYear === null) return;
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/calendario-cierre/', {
                params: { mes: selectedMonth + 1, anio: selectedYear }
            });
            const cal = response.data?.calendario;
            setEnPreparacion(!!response.data?.enPreparacion);
            if (cal) {
                setCalendarioId(cal.id);
                setCalendarioEstado(cal.estado || 'borrador');
                setCalendarioCierre(cal.items || []);
                setInicioOperaciones(cal.inicioOperaciones || '');
                setInicioAnunciado(!!cal.inicioAnunciado);
                setInicioComentario(cal.inicioComentario || null);
            } else {
                setCalendarioId(null); setCalendarioEstado('borrador');
                setCalendarioCierre([]); setInicioOperaciones('');
                setInicioAnunciado(false); setInicioComentario(null);
            }
        } catch (error) {
            console.error('Error fetching calendario:', error);
            setCalendarioId(null); setCalendarioCierre([]);
        } finally { setLoading(false); }
    }, [selectedMonth, selectedYear]);

    useEffect(() => { fetchCalendario(); }, [fetchCalendario]);

    // Fetch plantilla
    const fetchPlantilla = useCallback(async () => {
        setLoadingPlantilla(true);
        try {
            const response = await axiosInstance.get('/api/calendario-cierre/plantilla');
            setPlantillaItems(conEsperas(response.data?.items || []));
            setPlantillaModificada(false);
        } catch (error) { console.error('Error fetching plantilla:', error); }
        finally { setLoadingPlantilla(false); }
    }, []);

    useEffect(() => { if (currentView === 'plantilla') fetchPlantilla(); }, [currentView, fetchPlantilla]);

    // Por fecha y hora de cierre (asi la cadena de prelacion se lee de arriba a abajo);
    // los que no tienen fecha van al final. Asi se ve en la agenda y asi sale en el Excel.
    const calendarioOrdenado = useMemo(() => [...calendarioCierre].sort((a, b) => {
        if (!a.fechaCierre && !b.fechaCierre) return 0;
        if (!a.fechaCierre) return 1;
        if (!b.fechaCierre) return -1;
        if (a.fechaCierre !== b.fechaCierre) return a.fechaCierre < b.fechaCierre ? -1 : 1;
        return (a.horaEntrega || '99:99').localeCompare(b.horaEntrega || '99:99');
    }), [calendarioCierre]);
    // La agenda va por días
    const prelacion = useMemo(() => calcularPrelacion(calendarioCierre), [calendarioCierre]);
    // Quiénes esperan a cada renglón (lo inverso de dependeDe)
    const dependientes = useMemo(() => {
        const mapa = new Map();
        calendarioCierre.forEach(it => (prelacion.get(it.id)?.deps || []).forEach(d => {
            if (!mapa.has(d.id)) mapa.set(d.id, []);
            mapa.get(d.id).push(it.id);
        }));
        return mapa;
    }, [calendarioCierre, prelacion]);
    // Un color por renglón que hace esperar a otros, en el orden del calendario
    const colorPorFuente = useMemo(() => {
        const mapa = new Map();
        calendarioOrdenado.filter(it => dependientes.has(it.id))
            .forEach((it, i) => mapa.set(it.id, COLORES_CARRIL[i % COLORES_CARRIL.length]));
        return mapa;
    }, [calendarioOrdenado, dependientes]);
    const colorDe = useCallback((id) => colorPorFuente.get(id) || '#94a3b8', [colorPorFuente]);
    // Dos tablas, cada una por días: arriba el cierre de integración (los renglones
    // encadenados por la prelación) y abajo el cierre contable (los que no esperan
    // a nadie y a los que nadie espera)
    const tablas = useMemo(() => {
        const enPrelacion = (it) => (prelacion.get(it.id)?.deps.length || 0) > 0 || dependientes.has(it.id);
        const agenda = calendarioOrdenado.filter(it => it.departamentoId !== DEPTO_INICIO);
        const integracion = agenda.filter(enPrelacion);
        const contable = agenda.filter(it => !enPrelacion(it));
        return [
            { clave: 'integracion', titulo: 'Cierre de integración', detalle: 'Renglones encadenados: cada uno se habilita cuando cierran los que espera.', items: integracion, grupos: porDia(integracion) },
            { clave: 'contable', titulo: 'Cierre contable', items: contable, grupos: porDia(contable) },
        ].filter(t => t.items.length > 0);
    }, [calendarioOrdenado, prelacion, dependientes]);
    const itemsInicio = useMemo(() => calendarioOrdenado.filter(it => it.departamentoId === DEPTO_INICIO), [calendarioOrdenado]);
    const gruposInicio = useMemo(() => porDia(itemsInicio), [itemsInicio]);
    // Grafo de prelación en el margen: solo los renglones del cierre de integración
    const filasAgenda = useMemo(() => tablas.find(t => t.clave === 'integracion')?.items || [], [tablas]);
    const agendaRef = useRef(null);
    const nodosRef = useRef(new Map());
    const grafo = useMemo(() => aristasDelGrafo(filasAgenda, prelacion, colorDe), [filasAgenda, prelacion, colorDe]);
    // Con prelación, la agenda deja libre a su derecha el ancho del grafo (más sus márgenes)
    const reservaGrafo = grafo.aristas.length > 0 ? anchoGrafo(grafo.carriles) + GRAFO.margen * 2 : 0;
    const registrarNodo = useCallback((id, el) => {
        if (el) nodosRef.current.set(id, el);
        else nodosRef.current.delete(id);
    }, []);
    // Al pasar el mouse por un renglón con prelación se resalta su cadena y el resto se atenúa
    const [foco, setFoco] = useState(null);
    const focoTimer = useRef(null);
    const enfocar = useCallback((id) => {
        clearTimeout(focoTimer.current);
        focoTimer.current = setTimeout(() => setFoco(id), id ? 120 : 60);
    }, []);
    useEffect(() => () => clearTimeout(focoTimer.current), []);
    const cadena = useMemo(() => {
        if (!foco) return null;
        const c = cadenaDe(foco, prelacion, dependientes);
        return c.size > 1 ? c : null;
    }, [foco, prelacion, dependientes]);
    const nextMonth = selectedMonth === 11 ? 0 : selectedMonth + 1;
    const nextYear = selectedMonth === 11 ? selectedYear + 1 : selectedYear;

    const goToPreviousMonth = () => {
        setDireccionMes(-1);
        if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(selectedYear - 1); }
        else setSelectedMonth(selectedMonth - 1);
    };
    const goToNextMonth = () => {
        setDireccionMes(1);
        if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(selectedYear + 1); }
        else setSelectedMonth(selectedMonth + 1);
    };

    // ========== CALENDARIO HANDLERS ==========

    const handleCreateCalendario = async (desdePlantilla = false) => {
        setSaving(true);
        try {
            const response = await axiosInstance.post('/api/calendario-cierre/', {
                mes: selectedMonth + 1, anio: selectedYear, desdePlantilla
            });
            const cal = response.data?.calendario;
            if (cal) aplicarCalendario(cal);
        } catch (error) {
            alert(error.response?.data?.error || 'Error al crear el calendario');
        } finally { setSaving(false); }
    };

    const handleAplicarPlantilla = async () => {
        if (!calendarioId) return;
        setSaving(true);
        try {
            const response = await axiosInstance.post(`/api/calendario-cierre/${calendarioId}/aplicar-plantilla`);
            const cal = response.data?.calendario;
            if (cal) setCalendarioCierre(cal.items || []);
        } catch (error) {
            alert(error.response?.data?.error || 'Error al aplicar la plantilla');
        } finally { setSaving(false); }
    };

    const handleInicioOperacionesChange = async (value) => {
        setInicioOperaciones(value);
        if (!calendarioId) return;
        try {
            await axiosInstance.put(`/api/calendario-cierre/${calendarioId}`, { inicioOperaciones: value || null });
        } catch (error) { console.error('Error updating inicio operaciones:', error); }
    };

    const handleAnunciar = async () => {
        if (!calendarioId) return;
        if (!window.confirm('Se notificara a todos los responsables y se iniciara el proceso de cierre. ¿Continuar?')) return;
        setSaving(true);
        try {
            const response = await axiosInstance.post(`/api/calendario-cierre/${calendarioId}/anunciar`);
            const cal = response.data?.calendario;
            if (cal) {
                setCalendarioEstado(cal.estado);
                setCalendarioCierre(cal.items || []);
            }
        } catch (error) {
            alert(error.response?.data?.error || 'Error al anunciar');
        } finally { setSaving(false); }
    };

    // Textos del anuncio que recibira todo el personal. Deben coincidir con los
    // que arma el backend en /anunciar-inicio.
    const buildAnuncioInicio = () => {
        const mesCierre = `${MESES[selectedMonth]} ${selectedYear}`;
        let mesInicio, anioInicio, fechaStr;
        if (inicioOperaciones) {
            const d = new Date(inicioOperaciones + 'T12:00:00');
            mesInicio = MESES[d.getMonth()];
            anioInicio = d.getFullYear();
            fechaStr = d.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } else {
            mesInicio = MESES[nextMonth];
            anioInicio = nextYear;
            fechaStr = '';
        }
        const cola = fechaStr ? ` a partir del ${fechaStr}` : '';
        return {
            titulo: `Cierre ${mesCierre} concluido`,
            mensaje: `Se les informa que el cierre de ${mesCierre} ha concluido exitosamente. Pueden dar inicio de operaciones del mes de ${mesInicio} ${anioInicio}${cola}.`,
            resumen: `Inicio de operaciones de ${mesInicio} ${anioInicio}${cola}.`,
        };
    };

    const handleAnunciarInicio = () => {
        if (!calendarioId || !inicioOperaciones) return;
        setComentarioAnuncio('');
        setAnuncioModal(true);
    };

    const confirmarAnuncioInicio = async () => {
        if (!calendarioId || !inicioOperaciones) return;
        setSaving(true);
        try {
            const comentario = comentarioAnuncio.trim();
            const response = await axiosInstance.post(`/api/calendario-cierre/${calendarioId}/anunciar-inicio`, { comentario });
            const cal = response.data?.calendario;
            if (cal) {
                setInicioAnunciado(!!cal.inicioAnunciado);
                setInicioComentario(cal.inicioComentario || null);
            }
            setAnuncioModal(false);
        } catch (error) {
            alert(error.response?.data?.error || 'Error al anunciar el inicio de operaciones');
        } finally { setSaving(false); }
    };

    const handleReiniciar = async () => {
        if (!calendarioId) return;
        if (!window.confirm('Se eliminaran todos los departamentos del calendario. ¿Continuar?')) return;
        setSaving(true);
        try {
            const response = await axiosInstance.post(`/api/calendario-cierre/${calendarioId}/reiniciar`);
            const cal = response.data?.calendario;
            if (cal) {
                setCalendarioEstado(cal.estado || 'borrador');
                setCalendarioCierre(cal.items || []);
                setInicioOperaciones(cal.inicioOperaciones || '');
                setInicioAnunciado(!!cal.inicioAnunciado);
                setInicioComentario(cal.inicioComentario || null);
            }
        } catch (error) {
            alert(error.response?.data?.error || 'Error al reiniciar');
        } finally { setSaving(false); }
    };

    // Fetch historial comparativo
    const fetchHistorial = async () => {
        try {
            setHistorialLoading(true);
            const response = await axiosInstance.get('/api/calendario-cierre/historial?meses=12');
            setHistorial(response.data.historial || []);
        } catch (error) { console.error('Error fetching historial:', error); }
        finally { setHistorialLoading(false); }
    };

    // Fetch análisis IA
    const fetchAnalisis = async () => {
        try {
            setAnalisisLoading(true);
            setAnalisis(null);
            const response = await axiosInstance.get('/api/calendario-cierre/ai/analisis');
            setAnalisis(response.data);
        } catch (error) {
            setAnalisis({ error: error.response?.data?.error || 'Error al analizar' });
        }
        finally { setAnalisisLoading(false); }
    };

    useEffect(() => {
        if (currentView === 'historial') fetchHistorial();
    }, [currentView]);

    // Los cambios de un renglón se juntan y se guardan juntos: cambiar fecha y hora
    // seguidas no pierde la primera.
    const updateTimers = useRef({});
    const cambiosPendientes = useRef({});
    const handleInlineUpdate = useCallback((itemId, changes) => {
        setCalendarioCierre(prev => prev.map(item =>
            item.id === itemId ? { ...item, ...changes } : item
        ));

        cambiosPendientes.current[itemId] = { ...(cambiosPendientes.current[itemId] || {}), ...changes };
        if (updateTimers.current[itemId]) clearTimeout(updateTimers.current[itemId]);
        updateTimers.current[itemId] = setTimeout(async () => {
            const cambios = cambiosPendientes.current[itemId];
            delete cambiosPendientes.current[itemId];
            try {
                await axiosInstance.put(`/api/calendario-cierre/items/${itemId}`, cambios);
            } catch (error) { console.error('Error updating item:', error); }
        }, 600);
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta entrada?')) return;
        try {
            await axiosInstance.delete(`/api/calendario-cierre/items/${id}`);
            setCalendarioCierre(prev => prev.filter(item => item.id !== id));
        } catch (error) { alert('Error al eliminar'); }
    };

    // Check individual de responsable (para no-admin también)
    const toggleResponsableCheck = async (itemId, comentario = '') => {
        try {
            const response = await axiosInstance.patch(
                `/api/calendario-cierre/items/${itemId}/check-responsable`,
                { comentario }
            );
            const updatedItem = response.data?.item;
            if (updatedItem) {
                setCalendarioCierre(prev => prev.map(item => item.id === itemId ? updatedItem : item));
            }
            if (response.data?.calendarioEstado) {
                setCalendarioEstado(response.data.calendarioEstado);
            }
        } catch (error) {
            alert(error.response?.data?.error || 'Error al marcar check');
        }
    };

    // Prelación de un renglón del calendario: se guarda al momento (también con el
    // calendario ya anunciado, como los responsables)
    const handleGuardarEspera = async (itemId, ids) => {
        setGuardandoEspera(true);
        try {
            const response = await axiosInstance.put(`/api/calendario-cierre/items/${itemId}`, { dependeDe: ids });
            const updatedItem = response.data?.item;
            if (updatedItem) {
                setCalendarioCierre(prev => prev.map(item => item.id === itemId ? { ...item, dependeDe: updatedItem.dependeDe } : item));
            }
            setEsperaModal(null);
        } catch (error) {
            alert(error.response?.data?.error || 'No se pudo guardar a quién espera');
        } finally { setGuardandoEspera(false); }
    };

    // ========== PLANTILLA HANDLERS ==========

    // Contador para keys estables de renglones creados en el cliente (permite duplicados)
    const uidRef = useRef(0);
    const nextUid = () => `new-${uidRef.current++}`;

    const handleAddPlantillaItem = (deptId) => {
        setPlantillaItems(prev => [...prev, { _uid: nextUid(), departamentoId: deptId, nombre: '', responsableIds: [], indicaciones: '', orden: prev.length }]);
        setPlantillaModificada(true);
    };

    // Agregar un departamento escrito a mano (no está en el catálogo)
    const handleAddDeptPersonalizado = () => {
        const nombre = nuevoDeptNombre.trim();
        if (!nombre) return;
        setPlantillaItems(prev => [...prev, { _uid: nextUid(), departamentoId: `custom_${Date.now()}`, nombre, responsableIds: [], indicaciones: '', orden: prev.length }]);
        setPlantillaModificada(true);
        setNuevoDeptNombre('');
    };

    const handleUpdatePlantillaItem = (index, field, value) => {
        setPlantillaItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
        setPlantillaModificada(true);
    };

    const handleRemovePlantillaItem = (index) => {
        setPlantillaItems(prev => prev.filter((_, i) => i !== index));
        setPlantillaModificada(true);
    };

    const handleSavePlantilla = async () => {
        setSavingPlantilla(true);
        try {
            const posicion = new Map(plantillaItems.map((item, i) => [claveDe(item), i]));
            const response = await axiosInstance.put('/api/calendario-cierre/plantilla', {
                items: plantillaItems.map((item, i) => ({
                    departamentoId: item.departamentoId, nombre: (item.nombre || '').trim() || null,
                    responsableIds: item.responsableIds || [],
                    indicaciones: item.indicaciones || '', orden: i,
                    dependeDe: (item.esperaKeys || []).filter(k => posicion.has(k)).map(k => posicion.get(k)),
                }))
            });
            setPlantillaItems(conEsperas(response.data?.items || []));
            setPlantillaModificada(false);
        } catch (error) { alert(error.response?.data?.error || 'Error al guardar la plantilla'); }
        finally { setSavingPlantilla(false); }
    };

    const handleAddAllDepts = () => {
        const existentes = new Set(plantillaItems.map(p => p.departamentoId));
        const faltantes = DEPARTAMENTOS.filter(d => !existentes.has(d.id));
        if (faltantes.length === 0) return;
        setPlantillaItems(prev => [...prev, ...faltantes.map((d, i) => ({
            _uid: nextUid(), departamentoId: d.id, nombre: '', responsableIds: [], indicaciones: '', orden: prev.length + i
        }))]);
        setPlantillaModificada(true);
    };

    // ========== UTILS ==========

    const getDepartamentoNombre = (id) => {
        const dept = departamentos.find(d => d.id === id);
        return dept?.nombre || id;
    };

    // Excel con lo mismo que muestra la agenda (orden, responsables, cierre real y diferencia)
    const handleExportarExcel = async () => {
        setExportandoExcel(true);
        try {
            const XLSX = await import('xlsx');
            const nombreEmpleado = (id) => {
                const emp = empleados.find(e => e.id === id);
                return emp ? getFullName(emp) : '';
            };
            const filas = calendarioOrdenado.map(item => {
                const checksLog = item.checksLog || [];
                const quienCerro = checksLog.length > 0 ? checksLog[checksLog.length - 1] : null;
                const cerrado = formatFechaCierre(quienCerro?.fecha);
                const prel = prelacion.get(item.id);
                const diferencia = getDiferenciaCierre(item, quienCerro, ahora, prel);
                return {
                    Departamento: item.nombre || getDepartamentoNombre(item.departamentoId),
                    Responsables: (item.responsableIds || []).map(nombreEmpleado).filter(Boolean).join(', '),
                    Fecha: item.fechaCierre ? item.fechaCierre.split('-').reverse().join('/') : '',
                    Hora: hora12(item.horaEntrega),
                    Indicaciones: item.indicaciones || '',
                    'Espera a': (prel?.deps || []).map(d => `${nombreRenglon(d)} (${cuandoCierra(d, null)})`).join(', '),
                    'Cerrado el': cerrado ? `${cerrado.fecha} ${cerrado.hora}` : '',
                    'Cerrado por': quienCerro?.nombre || '',
                    Diferencia: diferencia.texto,
                    'Plazo corrido': diferencia.corrido?.texto || '',
                    Estado: item.completado ? 'Cerrado' : 'Pendiente',
                    Notas: (item.comentarios || []).map(c => `${c.nombre}: ${c.comentario}`).join('\n'),
                };
            });

            const hoja = XLSX.utils.json_to_sheet(filas);
            hoja['!autofilter'] = { ref: hoja['!ref'] };
            hoja['!cols'] = [28, 34, 12, 8, 60, 40, 20, 22, 18, 30, 11, 50].map(wch => ({ wch }));
            if (inicioOperaciones) {
                XLSX.utils.sheet_add_aoa(hoja, [[], ['Inicio de operaciones', inicioOperaciones.split('-').reverse().join('/')]], { origin: -1 });
            }

            const libro = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(libro, hoja, `Cierre ${MESES[selectedMonth]} ${selectedYear}`);
            XLSX.writeFile(libro, `Calendario de cierre ${MESES[selectedMonth]} ${selectedYear}.xlsx`);
        } catch (error) {
            console.error('Error exportando el calendario:', error);
            alert('No se pudo exportar el calendario a Excel');
        } finally {
            setExportandoExcel(false);
        }
    };

    const deptsFaltantesPlantilla = useMemo(() => {
        const existentes = new Set(plantillaItems.map(p => p.departamentoId));
        return DEPARTAMENTOS.filter(d => !existentes.has(d.id));
    }, [plantillaItems]);

    // Responsables de "Costos y Presupuesto" (empleado_ids) del calendario actual
    const costosResponsables = useMemo(() => {
        const ids = new Set();
        calendarioCierre.forEach(it => {
            if (it.departamentoId === 'costos') (it.responsableIds || []).forEach(id => ids.add(id));
        });
        return ids;
    }, [calendarioCierre]);

    // Gestores (admin/contabilidad) o los responsables de Costos y Presupuesto
    const puedeAnunciarInicio = isGestor || (user?.empleado_id != null && costosResponsables.has(user.empleado_id));
    // Las operaciones no arrancan sin la lista de precios de Planificación Financiera
    const esperaPlanificacion = itemsInicio.some(it => !it.completado);

    const renderParada = (item, i, lista) => (
        <ParadaRow
            key={item.id}
            item={item}
            index={i}
            primero={i === 0}
            ultimo={i === lista.length - 1}
            empleados={empleados}
            loadingEmpleados={loadingEmpleados}
            onUpdate={handleInlineUpdate}
            onDelete={handleDelete}
            onToggleResponsableCheck={toggleResponsableCheck}
            getDepartamentoNombre={getDepartamentoNombre}
            isSuperAdmin={isSuperAdmin}
            puedeGestionar={isGestor}
            currentEmpleadoId={user?.empleado_id}
            calendarioEstado={calendarioEstado}
            ahora={ahora}
            prel={prelacion.get(item.id)}
            onEditarEspera={(id) => setEsperaModal({ tipo: 'calendario', clave: id })}
            cadena={cadena}
            onFoco={enfocar}
            colorDe={colorDe}
            registrarNodo={registrarNodo}
        />
    );

    // Un día de la agenda: encabezado con la fecha y la tarjeta con sus renglones
    const renderGrupo = (grupo) => {
        const cerradosDia = grupo.items.filter(i => i.completado).length;
        const esHoy = grupo.fecha === hoyIso;
        return (
            <section key={grupo.fecha || 'sin-fecha'}>
                <div className="flex items-baseline justify-between gap-4 px-1 mb-3">
                    <h3 className="text-base font-semibold text-slate-900 first-letter:uppercase">
                        {grupo.fecha ? diaLargo(grupo.fecha) : 'Sin fecha'}
                        {esHoy && <span className="ml-2 align-middle inline-flex h-5 items-center rounded-full bg-sura-yellow/30 px-2 text-[11px] font-semibold text-slate-900">Hoy</span>}
                    </h3>
                    <span className="text-xs text-slate-400 tabular-nums">
                        {cerradosDia === grupo.items.length ? 'Todos cerrados' : `${cerradosDia} de ${grupo.items.length} cerrados`}
                    </span>
                </div>
                <div className={`${TARJETA} divide-y divide-slate-100`}>
                    {grupo.items.map((item, i) => renderParada(item, i, grupo.items))}
                </div>
            </section>
        );
    };

    // Editor de "a quién espera", para un renglón del calendario o de la plantilla
    const renderEsperaModal = () => {
        if (!esperaModal) return null;
        const cerrar = () => setEsperaModal(null);

        if (esperaModal.tipo === 'calendario') {
            const item = calendarioCierre.find(i => i.id === esperaModal.clave);
            if (!item) return null;
            const porId = new Map(calendarioCierre.map(i => [i.id, i]));
            const planItem = planDe(item);
            const opciones = calendarioOrdenado.filter(o => o.id !== item.id).map(o => {
                const planO = planDe(o);
                return {
                    clave: o.id,
                    nombre: nombreRenglon(o),
                    cuando: cuandoCierra(o, null),
                    indicaciones: (o.indicaciones || '').trim(),
                    bloqueada: esperaA(o.id, item.id, id => porId.get(id)?.dependeDe),
                    despues: !!(planItem && planO && planO > planItem),
                };
            });
            return (
                <EsperaModal key={`calendario-${item.id}`}
                    nombre={nombreRenglon(item)} cuando={cuandoCierra(item, null)}
                    opciones={opciones} seleccion={item.dependeDe || []} guardando={guardandoEspera}
                    onCerrar={cerrar} onGuardar={(ids) => handleGuardarEspera(item.id, ids)} />
            );
        }

        const item = plantillaItems.find(p => claveDe(p) === esperaModal.clave);
        if (!item) return null;
        const porClave = new Map(plantillaItems.map(p => [claveDe(p), p]));
        const opciones = plantillaItems.filter(p => p !== item).map(p => ({
            clave: claveDe(p),
            nombre: nombreRenglon(p),
            cuando: '',
            indicaciones: (p.indicaciones || '').trim(),
            bloqueada: esperaA(claveDe(p), claveDe(item), k => porClave.get(k)?.esperaKeys),
            despues: false,
        }));
        return (
            <EsperaModal key={`plantilla-${claveDe(item)}`}
                nombre={etiquetaPlantilla(item, plantillaItems)}
                opciones={opciones} seleccion={item.esperaKeys || []} guardando={false}
                onCerrar={cerrar}
                onGuardar={(keys) => {
                    setPlantillaItems(prev => prev.map(p => claveDe(p) === claveDe(item) ? { ...p, esperaKeys: keys } : p));
                    setPlantillaModificada(true);
                    cerrar();
                }} />
        );
    };

    // Non-admin users can view in read-only mode
    // isSuperAdmin = admin (todo), isGestor = admin + contabilidad (crear/anunciar)

    // ========== VISTA HISTORIAL ==========
    if (currentView === 'historial') {
        // Meses de izquierda (más viejo) a derecha; departamentos en filas
        const meses = [...historial].reverse();
        const nombresDepto = [];
        meses.slice().reverse().forEach(h => h.items.forEach(it => {
            if (!nombresDepto.includes(it.departamento)) nombresDepto.push(it.departamento);
        }));
        return (
            <div className="max-w-[1440px] mx-auto pb-16 pt-2 animate-fadeIn">
                <EncabezadoVista
                    titulo="Historial de cierres"
                    subtitulo="Qué departamentos cerraron en cada mes anunciado. Cada punto es un mes; pasa el cursor para ver el detalle."
                    onVolver={() => setCurrentView('calendario')}
                >
                    {isGestor && (
                        <button onClick={fetchAnalisis} disabled={analisisLoading || historial.length < 2} className={BTN.secundario}>
                            {analisisLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} className="text-violet-500" />}
                            Analizar con IA
                        </button>
                    )}
                </EncabezadoVista>

                {/* Panel IA */}
                {analisis && !analisis.error && (
                    <motion.section initial={reduce ? false : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        className={`${TARJETA} p-6 mb-8 relative overflow-hidden`}>
                        <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1 bg-violet-500" />
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                            <Sparkles size={16} className="text-violet-500" />
                            <h3 className="text-base font-semibold text-slate-900">Lectura de los últimos cierres</h3>
                            <span className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-xs font-medium ${
                                analisis.tendencia === 'mejorando' ? 'bg-emerald-50 text-emerald-700' :
                                analisis.tendencia === 'empeorando' ? 'bg-rose-50 text-rose-700' :
                                'bg-slate-100 text-slate-600'
                            }`}>
                                {analisis.tendencia === 'mejorando' && <TrendingUp size={12} />}
                                {analisis.tendencia === 'empeorando' && <TrendingDown size={12} />}
                                {analisis.tendencia === 'estable' && <Minus size={12} />}
                                {analisis.tendencia}
                            </span>
                        </div>
                        <p className="text-sm leading-relaxed text-slate-700 max-w-3xl">{analisis.resumen}</p>

                        <div className="mt-5 grid gap-6 lg:grid-cols-3">
                            {analisis.cuellos_botella?.length > 0 && (
                                <div className="lg:col-span-2">
                                    <h4 className="text-sm font-semibold text-slate-900 mb-2">Cuellos de botella</h4>
                                    <ul className="space-y-2">
                                        {analisis.cuellos_botella.map((cb, i) => (
                                            <li key={i} className="flex items-start gap-3 rounded-xl bg-slate-50 ring-1 ring-inset ring-slate-200/70 px-3 py-2.5 text-sm">
                                                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                                                    cb.severidad === 'alta' ? 'bg-rose-500' : cb.severidad === 'media' ? 'bg-amber-400' : 'bg-slate-300'
                                                }`} title={`Severidad ${cb.severidad}`} />
                                                <div className="flex-1 min-w-0">
                                                    <span className="font-medium text-slate-900">{cb.departamento}</span>
                                                    <span className="text-slate-500">: {cb.problema}</span>
                                                </div>
                                                <span className="text-xs text-slate-400 whitespace-nowrap">{cb.frecuencia}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="space-y-5">
                                {analisis.departamentos_ejemplares?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-900 mb-2">Ejemplares</h4>
                                        <div className="flex flex-wrap gap-1.5">
                                            {analisis.departamentos_ejemplares.map((d, i) => (
                                                <span key={i} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 ring-1 ring-inset ring-emerald-600/15">
                                                    <Check size={11} strokeWidth={3} />{d}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {analisis.recomendaciones?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-slate-900 mb-2">Recomendaciones</h4>
                                        <ul className="space-y-1.5">
                                            {analisis.recomendaciones.map((r, i) => (
                                                <li key={i} className="text-sm text-slate-600 pl-3 relative">
                                                    <span aria-hidden="true" className="absolute left-0 top-2 w-1 h-1 rounded-full bg-slate-400" />{r}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.section>
                )}
                {analisis?.error && (
                    <div className="mb-8 rounded-xl bg-rose-50 ring-1 ring-inset ring-rose-200 px-4 py-3 text-sm text-rose-700">{analisis.error}</div>
                )}

                {historialLoading ? (
                    <div className={`${TARJETA} p-6 space-y-3`}>
                        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-6 rounded-md bg-slate-100 skeleton" />)}
                    </div>
                ) : historial.length === 0 ? (
                    <EstadoVacio icono={History} titulo="Todavía no hay historial">
                        Aquí aparecen los calendarios anunciados o completados.
                    </EstadoVacio>
                ) : (
                    <div className={`${TARJETA} overflow-x-auto`}>
                        <table className="w-full text-sm border-separate border-spacing-0">
                            <thead>
                                <tr>
                                    <th className="sticky left-0 z-[1] bg-white text-left font-medium text-slate-500 px-5 py-4 border-b border-slate-100 min-w-[220px]">Departamento</th>
                                    {meses.map(h => (
                                        <th key={h.id} className="px-2 py-4 border-b border-slate-100 text-center font-normal min-w-[68px]">
                                            <span className="block text-xs font-semibold text-slate-800">{h.mesNombre.slice(0, 3)}</span>
                                            <span className="block text-[11px] text-slate-400 tabular-nums">{String(h.anio).slice(2)}</span>
                                            <span className={`mt-1 inline-block text-[11px] font-medium tabular-nums ${h.porcentaje === 100 ? 'text-emerald-600' : 'text-slate-500'}`}
                                                title={`${h.completados} de ${h.totalItems} cerrados${h.estado === 'anunciado' ? ', en curso' : ''}`}>
                                                {h.porcentaje}%
                                            </span>
                                        </th>
                                    ))}
                                    <th className="px-5 py-4 border-b border-slate-100 text-right font-medium text-slate-500 whitespace-nowrap">Cerró</th>
                                </tr>
                            </thead>
                            <tbody>
                                {nombresDepto.map(nombre => {
                                    const celdas = meses.map(h => h.items.find(it => it.departamento === nombre));
                                    const presentes = celdas.filter(Boolean);
                                    const cerro = presentes.filter(it => it.completado).length;
                                    return (
                                        <tr key={nombre} className="group">
                                            <td className="sticky left-0 z-[1] bg-white group-hover:bg-slate-50 px-5 py-2.5 border-b border-slate-50 font-medium text-slate-800 whitespace-nowrap transition-colors">{nombre}</td>
                                            {celdas.map((it, i) => (
                                                <td key={meses[i].id} className="px-2 py-2.5 border-b border-slate-50 text-center group-hover:bg-slate-50 transition-colors">
                                                    {!it ? (
                                                        <span className="inline-block w-2.5 h-px bg-slate-200 align-middle" title={`${meses[i].mesNombre} ${meses[i].anio}: no estaba en el calendario`} />
                                                    ) : (
                                                        <span
                                                            title={`${meses[i].mesNombre} ${meses[i].anio}: ${it.completado ? 'cerró' : 'no cerró'}${it.comentarios > 0 ? `, ${it.comentarios} nota${it.comentarios > 1 ? 's' : ''}` : ''}`}
                                                            className={`relative inline-block w-3 h-3 rounded-full align-middle ${it.completado ? 'bg-emerald-500' : 'ring-2 ring-inset ring-rose-400'}`}>
                                                            {it.comentarios > 0 && <span className="absolute -top-0.5 -right-1 w-1.5 h-1.5 rounded-full bg-slate-400 ring-1 ring-white" />}
                                                        </span>
                                                    )}
                                                </td>
                                            ))}
                                            <td className="px-5 py-2.5 border-b border-slate-50 text-right tabular-nums text-slate-500 whitespace-nowrap group-hover:bg-slate-50 transition-colors">
                                                <span className="font-medium text-slate-800">{cerro}</span> de {presentes.length}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="flex flex-wrap items-center gap-5 px-5 py-3 text-xs text-slate-500 border-t border-slate-100">
                            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Cerró</span>
                            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full ring-2 ring-inset ring-rose-400" />No cerró</span>
                            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Con notas</span>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ========== VISTA PLANTILLA ==========
    if (currentView === 'plantilla') {
        return (
            <div className="max-w-[1440px] mx-auto pb-28 pt-2 animate-fadeIn">
                <EncabezadoVista
                    titulo="Plantilla de cierre"
                    subtitulo="La base con la que se arma cada mes: departamentos, responsables, indicaciones y a quién espera cada uno. Las fechas y horas se ponen en el calendario."
                    onVolver={() => setCurrentView('calendario')}
                >
                    {deptsFaltantesPlantilla.length > 0 && plantillaItems.length > 0 && (
                        <button onClick={handleAddAllDepts} className={BTN.secundario}>
                            <Plus size={15} />Agregar los que faltan
                        </button>
                    )}
                </EncabezadoVista>

                {loadingPlantilla ? (
                    <div className={`${TARJETA} p-6 space-y-4`}>
                        {[1, 2, 3, 4].map(i => <div key={i} className="h-12 rounded-lg bg-slate-100 skeleton" />)}
                    </div>
                ) : plantillaItems.length === 0 ? (
                    <EstadoVacio icono={ClipboardList} titulo="La plantilla está vacía">
                        <p>Agrega los departamentos que cierran cada mes.</p>
                        <button onClick={handleAddAllDepts} className={`${BTN.primario} mt-5`}>
                            <Plus size={15} />Agregar todos los departamentos
                        </button>
                    </EstadoVacio>
                ) : (
                    <div className={TARJETA}>
                        <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_240px_minmax(0,1.4fr)_40px] gap-6 px-6 py-3 text-xs font-medium text-slate-500 border-b border-slate-100">
                            <span>Departamento</span><span>Responsables</span><span>Indicaciones</span><span />
                        </div>
                        <div className="divide-y divide-slate-100">
                            {plantillaItems.map((item, index) => (
                                <motion.div key={item.id ?? item._uid}
                                    initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.25, delay: Math.min(index, 12) * 0.025 }}
                                    className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px_minmax(0,1.4fr)_40px] gap-x-6 gap-y-3 px-6 py-4 items-start group">
                                    <div className="min-w-0">
                                        <input type="text" value={item.nombre || ''}
                                            onChange={(e) => handleUpdatePlantillaItem(index, 'nombre', e.target.value)}
                                            placeholder={DEPARTAMENTOS.find(d => d.id === item.departamentoId)?.nombre || 'Nombre del departamento'}
                                            title="Puedes renombrarlo (por ejemplo, dos procesos distintos del mismo departamento)"
                                            className="w-full -mx-2 px-2 h-9 rounded-lg bg-transparent text-[15px] font-semibold text-slate-900 placeholder:text-slate-900 outline-none hover:bg-slate-50 focus:bg-white focus:ring-2 focus:ring-sura-blue/30 transition-colors" />
                                        <EsperaChips
                                            esperas={(item.esperaKeys || [])
                                                .map(k => plantillaItems.find(p => claveDe(p) === k))
                                                .filter(Boolean)
                                                .map(p => ({ clave: claveDe(p), nombre: etiquetaPlantilla(p, plantillaItems), detalle: (p.indicaciones || '').trim() }))}
                                            puedeEditar
                                            onEditar={() => setEsperaModal({ tipo: 'plantilla', clave: claveDe(item) })}
                                            className="mt-1" />
                                    </div>
                                    <div className="min-w-0 lg:pt-0.5">
                                        <ResponsablePopover empleados={empleados} value={item.responsableIds || []}
                                            onChange={(ids) => handleUpdatePlantillaItem(index, 'responsableIds', ids)} loading={loadingEmpleados} />
                                    </div>
                                    <textarea value={item.indicaciones || ''}
                                        onChange={(e) => handleUpdatePlantillaItem(index, 'indicaciones', e.target.value)}
                                        rows={2}
                                        className="w-full px-3 py-2 rounded-lg bg-slate-50 text-sm text-slate-700 ring-1 ring-inset ring-slate-200/70 outline-none focus:bg-white focus:ring-2 focus:ring-sura-blue/30 resize-y transition-colors"
                                        placeholder="Qué tiene que entregar este departamento" />
                                    <div className="flex lg:justify-center lg:pt-0.5">
                                        <button onClick={() => handleRemovePlantillaItem(index)}
                                            className={`${BTN.icono} hover:bg-rose-50 hover:text-rose-600 lg:opacity-0 lg:group-hover:opacity-100 focus:opacity-100`}
                                            title="Quitar de la plantilla"><Trash2 size={15} /></button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Agregar */}
                        <div className="border-t border-slate-100 bg-slate-50/60 rounded-b-2xl px-6 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                                <select value="" onChange={(e) => { if (e.target.value) handleAddPlantillaItem(e.target.value); }}
                                    className={`${CAMPO_BASE} flex-1 min-w-[200px]`}>
                                    <option value="">Agregar uno de la lista</option>
                                    {DEPARTAMENTOS.map(dept => <option key={dept.id} value={dept.id}>{dept.nombre}</option>)}
                                </select>
                                <span className="text-xs text-slate-400 px-1">o</span>
                                <input type="text" value={nuevoDeptNombre}
                                    onChange={(e) => setNuevoDeptNombre(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddDeptPersonalizado(); }}
                                    placeholder="Escribe uno nuevo"
                                    className={`${CAMPO_BASE} flex-1 min-w-[200px]`} />
                                <button onClick={handleAddDeptPersonalizado} disabled={!nuevoDeptNombre.trim()} className={BTN.secundario}>
                                    <Plus size={15} />Agregar
                                </button>
                            </div>
                            <p className="mt-2 text-xs text-slate-400">Un departamento puede estar más de una vez, con otro nombre (por ejemplo, dos procesos distintos de Comercializacion).</p>
                        </div>
                    </div>
                )}

                {/* Guardar: aparece cuando hay cambios */}
                <AnimatePresence>
                    {plantillaModificada && (
                        <motion.div key="guardar"
                            initial={reduce ? false : { opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
                            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                            className="sticky bottom-6 z-30 mt-6 flex justify-center pointer-events-none">
                            <div className="pointer-events-auto flex items-center gap-4 rounded-2xl bg-slate-900 pl-5 pr-2 py-2 shadow-2xl shadow-slate-900/25">
                                <span className="text-sm text-slate-200">Tienes cambios sin guardar</span>
                                <button onClick={handleSavePlantilla} disabled={savingPlantilla}
                                    className="inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors disabled:opacity-50">
                                    {savingPlantilla ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                                    {savingPlantilla ? 'Guardando' : 'Guardar plantilla'}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {renderEsperaModal()}
            </div>
        );
    }

    // ========== VISTA CALENDARIO ==========
    if (initialLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-slate-400" size={28} />
            </div>
        );
    }

    const puedeExportar = (isGestor || puedeAnunciarInicio) && calendarioCierre.length > 0;
    const hoyIso = isoLocal(new Date(ahora));
    const fechaInicioLarga = inicioOperaciones ? diaLargo(inicioOperaciones) : null;

    return (
        <div className="max-w-[1440px] mx-auto pb-16 pt-2 animate-fadeIn">
            {/* Encabezado: el mes y lo que se puede hacer */}
            <header className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4 min-w-0">
                    <div className="flex items-center rounded-lg bg-white ring-1 ring-slate-200 shadow-sm shrink-0">
                        <button onClick={goToPreviousMonth} aria-label="Mes anterior"
                            className="w-9 h-9 flex items-center justify-center rounded-l-lg text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400">
                            <ChevronLeft size={18} />
                        </button>
                        <span className="w-px h-5 bg-slate-200" />
                        <button onClick={goToNextMonth} aria-label="Mes siguiente"
                            className="w-9 h-9 flex items-center justify-center rounded-r-lg text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400">
                            <ChevronRight size={18} />
                        </button>
                    </div>
                    <div className="overflow-hidden">
                        <AnimatePresence mode="wait" initial={false} custom={direccionMes}>
                            <motion.h1 key={`${selectedMonth}-${selectedYear}`}
                                custom={direccionMes}
                                initial={reduce ? false : { opacity: 0, x: direccionMes * 16 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={reduce ? { opacity: 0 } : { opacity: 0, x: direccionMes * -16 }}
                                transition={{ duration: 0.18 }}
                                className="text-2xl font-bold tracking-tight text-slate-900 whitespace-nowrap">
                                Cierre de {MESES[selectedMonth]} <span className="font-semibold text-slate-400">{selectedYear}</span>
                            </motion.h1>
                        </AnimatePresence>
                    </div>
                    {calendarioId && !loading && <EstadoPill estado={calendarioEstado} />}
                </div>
                <div className="flex items-center gap-2">
                    {puedeExportar && (
                        <button onClick={handleExportarExcel} disabled={exportandoExcel} className={BTN.secundario}>
                            {exportandoExcel ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
                            Excel
                        </button>
                    )}
                    <button onClick={() => setCurrentView('historial')} className={BTN.secundario}>
                        <BarChart3 size={15} />Historial
                    </button>
                    {isGestor && (
                        <button onClick={() => setCurrentView('plantilla')} className={BTN.secundario}>
                            <Settings size={15} />Plantilla
                        </button>
                    )}
                </div>
            </header>

            {loading ? (
                <div className="space-y-6">
                    <div className={`${TARJETA} h-56 skeleton`} />
                    <div className={`${TARJETA} p-5 space-y-4`}>
                        {[1, 2, 3].map(i => <div key={i} className="h-14 rounded-lg bg-slate-100 skeleton" />)}
                    </div>
                </div>
            ) : !calendarioId ? (
                isGestor ? (
                    <EstadoVacio icono={CalendarClock} titulo={`No hay calendario para ${MESES[selectedMonth].toLowerCase()}`}>
                        <p>Arma el cierre de {MESES[selectedMonth]} {selectedYear} desde la plantilla o empieza de cero.</p>
                        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                            <button onClick={() => handleCreateCalendario(true)} disabled={saving} className={BTN.primario}>
                                {saving ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                                Crear desde la plantilla
                            </button>
                            <button onClick={() => handleCreateCalendario(false)} disabled={saving} className={BTN.secundario}>
                                <Plus size={15} />Crear vacío
                            </button>
                        </div>
                    </EstadoVacio>
                ) : enPreparacion ? (
                    <EstadoVacio icono={Hourglass} titulo="Todavía no se ha publicado">
                        El calendario de cierre de {MESES[selectedMonth]} {selectedYear} está en preparación.
                    </EstadoVacio>
                ) : (
                    <EstadoVacio icono={CalendarClock} titulo="No hay cierre en proceso">
                        No se ha creado un calendario de cierre para {MESES[selectedMonth]} {selectedYear}.
                    </EstadoVacio>
                )
            ) : calendarioCierre.length === 0 ? (
                <EstadoVacio icono={ClipboardList} titulo="El calendario está vacío">
                    {isGestor ? (
                        <>
                            <p>Carga los departamentos desde la plantilla.</p>
                            <button onClick={handleAplicarPlantilla} disabled={saving} className={`${BTN.primario} mt-5`}>
                                {saving ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                                Cargar desde la plantilla
                            </button>
                        </>
                    ) : (
                        <p>Todavía no hay departamentos cargados.</p>
                    )}
                </EstadoVacio>
            ) : (
                <>
                    <RutaCierre items={calendarioOrdenado} prelacion={prelacion} ahora={ahora}
                        inicioOperaciones={inicioOperaciones} inicioAnunciado={inicioAnunciado} estado={calendarioEstado} />

                    {/* Borrador: lo que hace el gestor antes de publicar */}
                    {isGestor && calendarioEstado === 'borrador' && (
                        <div className="mb-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-slate-300 bg-white/70 px-5 py-3.5">
                            <p className="text-sm text-slate-600">
                                Los cambios se guardan solos. Nadie más lo ve hasta que lo anuncies.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                                <button onClick={handleReiniciar} disabled={saving} className={`${BTN.fantasma} hover:bg-rose-50 hover:text-rose-600`}>
                                    <RotateCcw size={15} />Reiniciar
                                </button>
                                <button onClick={handleAplicarPlantilla} disabled={saving} className={BTN.secundario}>
                                    <Download size={15} />Cargar desde la plantilla
                                </button>
                                <span title={inicioOperaciones ? undefined : 'Pon la fecha de inicio de operaciones (al final) para poder anunciar'}>
                                    <button onClick={handleAnunciar} disabled={saving || !inicioOperaciones} className={BTN.primario}>
                                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Megaphone size={15} />}
                                        Anunciar calendario
                                    </button>
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Agenda por días; el grafo de prelación va en el margen de la derecha */}
                    <div ref={agendaRef} className="relative space-y-12 lg:mr-[var(--reserva-grafo)]"
                        style={{ '--reserva-grafo': `${reservaGrafo}px` }}>
                        {tablas.map(tabla => {
                            const cerradosTabla = tabla.items.filter(i => i.completado).length;
                            return (
                                <section key={tabla.clave}>
                                    <div className="flex items-end justify-between gap-4 px-1 mb-5">
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-900">{tabla.titulo}</h2>
                                            {tabla.detalle && <p className="mt-0.5 text-sm text-slate-500">{tabla.detalle}</p>}
                                        </div>
                                        <span className="text-xs text-slate-400 tabular-nums shrink-0">
                                            {cerradosTabla === tabla.items.length ? 'Todos cerrados' : `${cerradosTabla} de ${tabla.items.length} cerrados`}
                                        </span>
                                    </div>
                                    <div className="space-y-8">
                                        {tabla.grupos.map(renderGrupo)}
                                    </div>
                                </section>
                            );
                        })}

                        {/* Última sección: inicio de operaciones, con lo que debe cerrar antes y el anuncio */}
                        <section>
                            <div className="flex items-end justify-between gap-4 px-1 mb-5">
                                <h2 className="text-lg font-semibold text-slate-900">Inicio de operaciones</h2>
                                <span className="text-xs text-slate-400 shrink-0">{inicioAnunciado ? 'Anunciado a todo el personal' : 'Cuando todos cierren'}</span>
                            </div>
                            <div className="space-y-8">
                                {gruposInicio.map(renderGrupo)}
                                <div className={`${TARJETA_SIN_BORDE} overflow-hidden ${inicioAnunciado ? 'ring-emerald-600/25' : 'ring-slate-200/80'}`}>
                                    <div className="flex flex-wrap items-end gap-x-5 gap-y-4 px-5 py-5">
                                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${inicioAnunciado ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600'}`}>
                                            {inicioAnunciado ? <Check size={20} strokeWidth={2.5} /> : <Flag size={19} />}
                                        </div>
                                        <div className="flex-1 min-w-[200px]">
                                            <p className="text-sm text-slate-500">Se reanudan las operaciones</p>
                                            {isGestor && !inicioAnunciado ? (
                                                <input type="date" value={inicioOperaciones}
                                                    onChange={(e) => handleInicioOperacionesChange(e.target.value)}
                                                    className={`${CAMPO_BASE} mt-1 w-48 font-medium tabular-nums`} />
                                            ) : (
                                                <p className="mt-0.5 text-lg font-semibold text-slate-900 first-letter:uppercase">
                                                    {fechaInicioLarga || <span className="text-slate-400 font-normal text-base">Sin fecha definida</span>}
                                                </p>
                                            )}
                                        </div>
                                        {inicioAnunciado ? (
                                            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                                                <Check size={16} strokeWidth={2.5} />Cierre culminado y anunciado
                                            </span>
                                        ) : calendarioEstado !== 'borrador' && puedeAnunciarInicio && inicioOperaciones && (
                                            <button onClick={handleAnunciarInicio} disabled={saving || esperaPlanificacion} className={BTN.exito}
                                                title={esperaPlanificacion
                                                    ? 'Se habilita cuando cierre Planificación Financiera'
                                                    : 'Da por culminado el cierre y notifica a todo el personal el inicio de operaciones'}>
                                                {saving ? <Loader2 size={15} className="animate-spin" /> : <Megaphone size={15} />}
                                                Culminar cierre y anunciar inicio
                                            </button>
                                        )}
                                    </div>

                                    {/* Salvedad que acompañó al anuncio */}
                                    {inicioAnunciado && inicioComentario && (
                                        <div className="flex gap-2.5 border-t border-amber-200/70 bg-amber-50 px-5 py-3.5">
                                            <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                            <div className="min-w-0">
                                                <p className="text-xs font-semibold text-amber-700 mb-0.5">Comentario del anuncio</p>
                                                <p className="text-sm text-amber-900 whitespace-pre-wrap break-words">{inicioComentario}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        <GrafoPrelacion filas={filasAgenda} aristas={grafo.aristas} ancho={anchoGrafo(grafo.carriles)}
                            nodosRef={nodosRef} agendaRef={agendaRef} prelacion={prelacion} cadena={cadena} ahora={ahora} />
                    </div>
                </>
            )}

            {renderEsperaModal()}

            {/* Anuncio de inicio de operaciones: comentario opcional + vista previa */}
            <Modal isOpen={anuncioModal} onClose={() => !saving && setAnuncioModal(false)} size="lg">
                <Modal.Header onClose={() => !saving && setAnuncioModal(false)}>
                    <div className="flex items-center gap-3">
                        <Megaphone size={18} className="text-emerald-600" />
                        <span>Anunciar inicio de operaciones</span>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-6">
                    {(() => {
                        const anuncio = buildAnuncioInicio();
                        const comentario = comentarioAnuncio.trim();
                        return (
                            <div className="space-y-5">
                                <p className="text-sm text-slate-600">
                                    Esto da por culminado el cierre y le llega a <strong className="font-semibold text-slate-900">todo el personal</strong> por notificación y por correo.
                                </p>

                                <div>
                                    <div className="flex items-baseline justify-between mb-1.5">
                                        <label htmlFor="comentario-anuncio" className="text-sm font-medium text-slate-700">
                                            Comentario <span className="text-slate-400 font-normal">(opcional)</span>
                                        </label>
                                        <span className={`text-xs tabular-nums ${comentarioAnuncio.length > MAX_COMENTARIO_ANUNCIO - 60 ? 'text-amber-600' : 'text-slate-400'}`}>
                                            {comentarioAnuncio.length}/{MAX_COMENTARIO_ANUNCIO}
                                        </span>
                                    </div>
                                    <textarea id="comentario-anuncio" value={comentarioAnuncio}
                                        onChange={(e) => setComentarioAnuncio(e.target.value.slice(0, MAX_COMENTARIO_ANUNCIO))}
                                        rows={3} autoFocus
                                        className="w-full px-3 py-2.5 rounded-lg bg-white text-sm text-slate-800 ring-1 ring-inset ring-slate-200 outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                                        placeholder="Ej: Todos pueden iniciar operaciones menos Comercializacion, que aun no puede digitar pedidos." />
                                    <p className="mt-1.5 text-xs text-slate-500">
                                        Si lo escribes, va resaltado arriba del anuncio para que nadie lo pase por alto.
                                    </p>
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-500 mb-2">Así lo van a recibir</p>
                                    <div className="rounded-xl bg-slate-50 ring-1 ring-inset ring-slate-200 p-4">
                                        <h5 className="text-sm font-semibold text-slate-900">{anuncio.titulo}</h5>
                                        <AnimatePresence initial={false}>
                                            {comentario && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="mt-3 flex gap-2 rounded-lg bg-amber-50 ring-1 ring-inset ring-amber-200 px-3 py-2.5">
                                                        <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
                                                        <p className="text-sm text-amber-900 whitespace-pre-wrap break-words">{comentario}</p>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                            {comentario ? anuncio.resumen : anuncio.mensaje}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex gap-2 justify-end">
                        <button onClick={() => setAnuncioModal(false)} disabled={saving} className={BTN.fantasma}>Cancelar</button>
                        <button onClick={confirmarAnuncioInicio} disabled={saving} className={BTN.exito}>
                            {saving ? <Loader2 size={15} className="animate-spin" /> : <Megaphone size={15} />}
                            {saving ? 'Anunciando' : 'Anunciar a todo el personal'}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CalendarioCierreView;
